"use client";

import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Database,
  ExternalLink,
  FolderKanban,
  Info,
  RefreshCcw,
  Upload,
  X,
} from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CacheManagementModal } from "@/components/aicheck/CacheManagementModal";
import { useGracefulObservation } from "@/components/aicheck/hooks/useGracefulObservation";
import {
  type PendingShareBundleImport,
  useShareBundleImport,
} from "@/components/aicheck/hooks/useShareBundleImport";
import {
  type ActiveTab,
  isActiveTab,
  isTraceabilityTab,
  normalizeTraceabilityHistoryState,
  TRACEABILITY_HISTORY_KEY,
  useTraceabilityHistory,
} from "@/components/aicheck/hooks/useTraceabilityHistory";
import {
  openProjectDataOverviewPopupWindow,
  ProjectDataOverviewDialog,
  type ProjectDataOverviewResultMode,
} from "@/components/aicheck/ProjectDataOverviewDialog";
import { ProjectManagementBar } from "@/components/aicheck/ProjectManagementBar";
import { ResultsSummary } from "@/components/aicheck/ResultsSummary";
import { RunProgressGraph as RunProgress } from "@/components/aicheck/RunProgressGraph";
import {
  type ProjectDataComparisonResult,
  type ProjectDataRunInput,
  SourceWorkspace,
} from "@/components/aicheck/SourceWorkspace";
import { DialogShell } from "@/components/aicheck/shared/DialogShell";
import { BasicRequestsTab } from "@/components/aicheck/workspace/BasicRequestsTab";
import { ComparisonTab } from "@/components/aicheck/workspace/ComparisonTab";
import { ExpertKnowledgeTab } from "@/components/aicheck/workspace/ExpertKnowledgeTab";
import { FindingsTab } from "@/components/aicheck/workspace/FindingsTab";
import { FreeformTab } from "@/components/aicheck/workspace/FreeformTab";
import { HistoryTab } from "@/components/aicheck/workspace/HistoryTab";
import { NeedReviewTab } from "@/components/aicheck/workspace/NeedReviewTab";
import { RemindersTab } from "@/components/aicheck/workspace/RemindersTab";
import { useAICheckRun } from "@/hooks/useAICheckRun";
import { useDismissedChecks } from "@/hooks/useDismissedChecks";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useLocalResults } from "@/hooks/useLocalResults";
import { useRunCheckpoint } from "@/hooks/useRunCheckpoint";
import { buildRunResultFromCheckpoint } from "@/lib/aicheck/admin-rerun-planner";
import { apiUrl } from "@/lib/aicheck/api-base";
import {
  type AICheckShareBundleRestoreResult,
  createAICheckShareBundle,
  downloadAICheckShareBundle,
} from "@/lib/aicheck/cache-bundle";
import {
  type FreeformReviewResultCacheEntry,
  listFreeformReviewResultCaches,
  readLatestFreeformReviewResultCache,
} from "@/lib/aicheck/freeform-result-cache";
import {
  PROJECT_STAGE_LABELS,
  type ProjectRecord,
} from "@/lib/aicheck/history/types";
import { readStoredLang, writeStoredLang } from "@/lib/aicheck/lang-storage";
import {
  buildMergeCheckCacheKey,
  type MergeCheckResultCacheEntry,
  readMergeCheckResultCache,
} from "@/lib/aicheck/merge-check-cache";
import {
  projectDataComparisonFromSources,
  typedProjectDataComparison,
} from "@/lib/aicheck/project-data-comparison";
import type { ProjectDataOverviewIssue } from "@/lib/aicheck/project-data-issue-index";
import {
  isExternalProjectDataSource,
  isSameProjectDataSource,
} from "@/lib/aicheck/project-data-source";
import {
  projectTitleFromProjectData,
  projectTitleFromRunResult,
} from "@/lib/aicheck/project-title";
import { projectDetailPath } from "@/lib/aicheck/routes";
import {
  projectDataSourceSeed,
  projectIdFromProjectDataSource,
  readProjectDataSourceCache,
} from "@/lib/project-data-converters/client-cache";
import { authService } from "@/services/auth";
import { useAICheckStore } from "@/stores/aicheck";
import { useExpertFeedbackStore } from "@/stores/expertFeedback";
import type {
  AggregatedProjectData,
  AICheckRunResult,
  FreeformCheckResult,
  OutputLanguage,
  ProjectDataSourceDescriptor,
  RateLimitInfo,
  TraceabilityHistoryState,
  TraceabilityTab,
} from "@/types/aicheck";

interface RecentProjectItem {
  projectId: number;
  projectTitle: string;
  language?: OutputLanguage;
  sortAt: string;
  sourceLabel?: string;
  usesExternalProjectData?: boolean;
  hasCheckResult: boolean;
  freeformCache?: FreeformReviewResultCacheEntry;
}

type FreeformRestoreTarget = {
  templateId: string;
  prompt: string;
  cacheKey: string;
};

/**
 * 案件／外部入力を「開く」ときの選択状態更新を一本化するためのアクション記述子。
 *
 * 各ハンドラは「何をどうセットしたいか」だけをこの形で渡し、実際の setState は
 * {@link AICheckWorkspace} 内の applyWorkspaceSelection が単一の正準順序で適用する。
 *
 * 重要: フィールドが `undefined`（＝キーを渡さない）の場合、その setter は呼ばれない
 * （＝現状維持）。ハンドラ間で「意図的にそのフィールドを触らない」差分をそのまま
 * 表現できるようにするための設計。値として `null` を渡すのは「明示的にクリア」を意味する。
 *
 * 注: `reset()`（useAICheckRun）と runCheck / loadCachedResult / saveResult は
 * 実行順序に意味がある（reset の位置・await 前後）ため、この記述子には含めず
 * 各ハンドラ側に残す。ここで集約するのは順序非依存な純粋 setState 群のみ。
 */
interface WorkspaceSelectionAction {
  /** setProjectId(String(projectId)) */
  projectId?: number;
  selectedLanguage?: OutputLanguage;
  runLanguage?: OutputLanguage;
  /** writeStoredLang(projectId, language)（localStorage への永続化・順序非依存） */
  persistLanguage?: { projectId: number; language: OutputLanguage };
  activeProjectDataInput?: ProjectDataRunInput | null;
  freeformRestoreTarget?: FreeformRestoreTarget | null;
  freeformReviewResult?: FreeformCheckResult | null;
  sourceWorkspaceRestoreKey?: string | null;
  comparisonResult?: ProjectDataComparisonResult | null;
  activeTab?: ActiveTab;
  progressDismissed?: boolean;
}

function categoryToTraceabilityTab(
  category: ProjectDataOverviewIssue["category"],
): TraceabilityTab {
  if (category === "need_review") return "review";
  if (category === "reminders") return "reminders";
  return "basic";
}

function comparisonFromCachedResult(
  result: AICheckRunResult,
  cachedMergeCheck: MergeCheckResultCacheEntry | null,
): ProjectDataComparisonResult | null {
  return typedProjectDataComparison<
    ProjectDataComparisonResult["findings"][number]
  >(
    projectDataComparisonFromSources(
      { result },
      {
        comparison: cachedMergeCheck?.comparison,
        result: cachedMergeCheck?.result,
      },
    ),
  );
}

function projectDataInputFromCachedResult(
  result: AICheckRunResult,
): ProjectDataRunInput | null {
  // Restore is best-effort: a server-loaded result may reference client-side
  // caches (merge check, conversion workspace) that no longer exist in this
  // browser. Never let restore failure block displaying the result itself.
  try {
    return projectDataInputFromCachedResultUnsafe(result);
  } catch (e) {
    console.warn("[aicheck] Failed to restore external source workspace:", e);
    return null;
  }
}

function projectDataInputFromCachedResultUnsafe(
  result: AICheckRunResult,
): ProjectDataRunInput | null {
  const source = result.projectDataSource;
  if (!isExternalProjectDataSource(source)) return null;
  const mergeCheckContext = result.projectData.mergeCheckContext;
  const sourceCacheKey = source.origin?.cacheKey;
  const cachedMergeCheck = sourceCacheKey
    ? readMergeCheckResultCache(sourceCacheKey)
    : null;
  const comparison = comparisonFromCachedResult(result, cachedMergeCheck);
  const baseSource =
    mergeCheckContext?.baseSource ??
    comparison?.baseSource ??
    cachedMergeCheck?.metadata.baseSource;
  const candidateSource =
    mergeCheckContext?.candidateSource ??
    comparison?.candidateSource ??
    cachedMergeCheck?.metadata.candidateSource;
  const mergeCacheKey =
    cachedMergeCheck?.metadata.mergeCacheKey ??
    (baseSource && candidateSource
      ? (sourceCacheKey ??
        buildMergeCheckCacheKey({
          baseSource,
          candidateSource,
          language: result.language,
        }))
      : undefined);
  return {
    projectData: result.projectData,
    source,
    sourceLabel: source.label,
    seed: projectDataSourceSeed(source, String(result.projectId)),
    comparison: comparison ?? undefined,
    ...(cachedMergeCheck?.metadata
      ? { mergeCheckCache: cachedMergeCheck.metadata }
      : mergeCacheKey && baseSource && candidateSource
        ? {
            mergeCheckCache: {
              mergeCacheKey,
              rootMergeCacheKey: mergeCacheKey,
              stage: "merged",
              sourceLabel: source.label,
              seed: source.dataDigest || mergeCacheKey,
              baseSource,
              candidateSource,
            },
          }
        : undefined),
  };
}

function sourceWorkspaceRestoreKeyFor(
  input: ProjectDataRunInput | null,
  result?: AICheckRunResult | null,
): string | null {
  const source = input?.source;
  if (!source) return null;
  return [
    result?.projectId ?? source.origin?.crmProjectId ?? "external",
    result?.language ?? "",
    source.origin?.cacheKey ?? source.sourceId,
    result?.completedAt ?? input.seed,
  ].join(":");
}

function resultMatchesProjectId(
  result: AICheckRunResult | null | undefined,
  projectId: number | null,
): result is AICheckRunResult {
  return Boolean(result && projectId && result.projectId === projectId);
}

function buildProjectDataOnlyRunResult(params: {
  projectId: number;
  language: OutputLanguage;
  input: ProjectDataRunInput;
  comparison?: ProjectDataComparisonResult | null;
}): AICheckRunResult | null {
  if (!params.input.projectData) return null;
  const timestamp = new Date().toISOString();
  return {
    projectId: params.projectId,
    startedAt: timestamp,
    completedAt: timestamp,
    status: "completed",
    language: params.language,
    projectData: params.input.projectData,
    projectDataSource: params.input.source,
    projectDataComparison:
      params.input.comparison ?? params.comparison ?? undefined,
    aiResults: [],
    consistencyResults: [],
    customerProfileResult: null,
    totalTokensUsed: { prompt: 0, completion: 0, total: 0 },
    graphSkeleton: { nodes: [], edges: [] },
    checkLaneMap: {},
    plannedChecks: [],
    plannedCheckIds: [],
    plannedCheckNameById: {},
    checkBatchGroups: [],
    independentConsistencyRules: [],
    profileConsistencyRules: [],
  };
}

function recentProjectItemKey(item: {
  projectId: number;
  language?: OutputLanguage;
}): string {
  return `${item.projectId}:${item.language ?? ""}`;
}

function recentProjectSourceKey(
  source: AICheckRunResult["projectDataSource"],
  language: OutputLanguage | undefined,
  fallbackProjectId: number,
): string | null {
  if (!source) return null;
  const seed = projectDataSourceSeed(source, String(fallbackProjectId));
  return seed ? `${language ?? ""}:${seed}` : null;
}

function mergeRecentProjectItem(
  items: Map<string, RecentProjectItem>,
  item: RecentProjectItem,
): void {
  const key = recentProjectItemKey(item);
  const current = items.get(key);
  if (!current) {
    items.set(key, item);
    return;
  }

  const base = item.hasCheckResult && !current.hasCheckResult ? item : current;
  items.set(key, {
    ...base,
    sortAt:
      item.sortAt.localeCompare(current.sortAt) > 0
        ? item.sortAt
        : current.sortAt,
    sourceLabel: base.sourceLabel ?? current.sourceLabel ?? item.sourceLabel,
    usesExternalProjectData:
      current.usesExternalProjectData || item.usesExternalProjectData,
    hasCheckResult: current.hasCheckResult || item.hasCheckResult,
    freeformCache: current.freeformCache ?? item.freeformCache,
  });
}

function TabIssueBadges({
  counts,
}: {
  counts: { fail: number; warning: number };
}) {
  if (counts.fail === 0 && counts.warning === 0) return null;
  return (
    <span className="inline-flex items-center gap-0.5 pointer-events-none">
      {counts.fail > 0 && (
        <span className="bg-red-100 text-red-600 h-4 px-1 text-[10px] inline-flex items-center rounded-sm">
          {counts.fail}
        </span>
      )}
      {counts.warning > 0 && (
        <span className="bg-yellow-100 text-yellow-600 h-4 px-1 text-[10px] inline-flex items-center rounded-sm">
          {counts.warning}
        </span>
      )}
    </span>
  );
}

export interface AICheckWorkspaceProps {
  /** "entry" = 従来のトップページ動作 / "project" = /projects/[id] 埋め込み */
  variant?: "entry" | "project";
  /** variant="project" 時の対象案件（displayId 数値） */
  initialProjectId?: number | null;
  /** ProjectManagementBar を隠す（親ページにヘッダ編集がある場合） */
  showManagementBar?: boolean;
  /** false のとき結果閲覧専用にし、新規実行・再実行の導線を隠す */
  allowRunActions?: boolean;
  /** 実行が terminal (completed/aborted/error 等) に達した時に呼ぶ */
  onRunFinished?: () => void;
  /**
   * コミット済みサーバ結果が無い CRM 案件（page 側 notFound-CRM 分岐）で true。
   * true のとき、マウント時に取り込みダイアログを開く（ライブ実行中なら譲る）。
   * 開くのは「権威シグナル(=このprop)」、閉じるのはユーザー操作のみ。
   */
  promptIngestOnMount?: boolean;
  /**
   * 観測すべき実行の runId。親（案件詳細ページ）がサーバに問い合わせて
   * 得た値。設定されている間、この実行の進捗を表示する。
   *
   * **reconnectRun では届かない実行がある。** あちらは呼び出し元の JWT から
   * 作った scopeKey で探すため、POST /api/aicheck/runs や MCP で始まった
   * 実行は見つからない。runId で直接観測すれば、誰がどこから始めても繋がる。
   */
  observeRunId?: string | null;
  /** 観測する実行の言語。実行と表示の言語がずれるのを防ぐ */
  observeLanguage?: OutputLanguage | null;
  /**
   * 実行の観測は親が持つ（`observeRunId` で指示する）。
   *
   * **`observeRunId` の有無では判断できない。** 親がサーバに問い合わせて
   * runId を得るまでの間、この prop は null であって「実行が無い」とは
   * 区別できない。その隙間に自前で繋ぎ直すと、同じ実行に観測が二本張られ、
   * 片方の startRerun がもう片方の積み上げを実行中に消す（実際に
   * `/run/state` と `/runs/{id}/events` が二重に飛んでいた）。
   * 所有権は最初のレンダーから確定していなければならない。
   */
  parentOwnsObservation?: boolean;
}

export function AICheckWorkspace({
  variant = "entry",
  initialProjectId = null,
  showManagementBar = true,
  allowRunActions = true,
  onRunFinished,
  promptIngestOnMount = false,
  observeRunId = null,
  observeLanguage = null,
  parentOwnsObservation = false,
}: AICheckWorkspaceProps = {}) {
  const searchParams = useSearchParams();
  const {
    runCheck,
    runSelectedChecks,
    reconnectRun,
    observeRunProgress,
    resumeFromCheckpoint,
    showCheckpointAsAborted,
    adminRerunFromNode,
    toggleAbortFromHere,
    abortNode,
    abortRun,
    cancelRun,
    loadCachedResult,
    reset,
    projectId,
    setProjectId,
    runProgress,
    currentResult,
    previousResult,
    partialRunData,
  } = useAICheckRun();

  const {
    getHistory,
    getResult,
    findResultByProjectDataSource,
    clearAllResults,
    saveResult,
  } = useLocalResults();
  const checkpoint = useRunCheckpoint();
  const { isDismissed } = useDismissedChecks(Number(projectId) || 0);
  const adminStatus = useIsAdmin();
  // handleLoadProject の依存に prop を足すと再生成でマウント処理が
  // 走り直すため、ref で最新値だけ見る
  const observeRunIdRef = useRef<string | null>(observeRunId);
  observeRunIdRef.current = observeRunId;
  const [selectedLanguage, setSelectedLanguageState] =
    useState<OutputLanguage>("en");
  const [runLanguage, setRunLanguage] = useState<OutputLanguage>("en");
  const [activeTab, setActiveTab] = useState<ActiveTab>("basic");
  const [showUsageInfo, setShowUsageInfo] = useState(false);
  const [showCacheManagement, setShowCacheManagement] = useState(false);
  const [showProjectDataOverview, setShowProjectDataOverview] = useState(false);
  const [projectDataOverviewWindow, setProjectDataOverviewWindow] =
    useState<Window | null>(null);
  const projectDataOverviewWindowRef = useRef<Window | null>(null);
  const [projectDataOverviewFocusSource, setProjectDataOverviewFocusSource] =
    useState<string | null>(null);
  const [projectDataOverviewFocusKey, setProjectDataOverviewFocusKey] =
    useState(0);
  const [projectDataOverviewMode, setProjectDataOverviewMode] =
    useState<ProjectDataOverviewResultMode>("check");
  const processedIdRef = useRef<string | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const pendingRetryCheckIdsRef = useRef<string[]>([]);
  const shareBundleInputRef = useRef<HTMLInputElement | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(
    null,
  );
  const [progressDismissed, setProgressDismissed] = useState(false);
  const [activeProjectDataInput, setActiveProjectDataInput] =
    useState<ProjectDataRunInput | null>(null);
  const [freeformRestoreTarget, setFreeformRestoreTarget] =
    useState<FreeformRestoreTarget | null>(null);
  const [freeformReviewResult, setFreeformReviewResult] =
    useState<FreeformCheckResult | null>(null);
  const [sourceWorkspaceRestoreKey, setSourceWorkspaceRestoreKey] = useState<
    string | null
  >(null);
  const [comparisonResult, setComparisonResult] =
    useState<ProjectDataComparisonResult | null>(null);
  const [shareBundleStatus, setShareBundleStatus] = useState<string | null>(
    null,
  );

  // 案件／外部入力の「開く」系ハンドラが分散して行っていた選択状態更新を一本化する。
  // 各ハンドラは意図（WorkspaceSelectionAction）だけを渡し、実際の setState はここで
  // 単一の正準順序で適用する。個々の setter は互いの値を参照しない純粋な setState の
  // ため、テキスト上の順序が変わっても最終コミット状態は不変（＝挙動維持）。
  // undefined のフィールドは setter を呼ばない（＝現状維持）ので、ハンドラ間の
  // 「意図的に触らない」差分をそのまま保持できる。
  const applyWorkspaceSelection = useCallback(
    (action: WorkspaceSelectionAction) => {
      if (action.persistLanguage) {
        writeStoredLang(
          action.persistLanguage.projectId,
          action.persistLanguage.language,
        );
      }
      if (action.projectId !== undefined) {
        setProjectId(String(action.projectId));
      }
      if (action.selectedLanguage !== undefined) {
        setSelectedLanguageState(action.selectedLanguage);
      }
      if (action.runLanguage !== undefined) {
        setRunLanguage(action.runLanguage);
      }
      if (action.activeProjectDataInput !== undefined) {
        setActiveProjectDataInput(action.activeProjectDataInput);
      }
      if (action.freeformRestoreTarget !== undefined) {
        setFreeformRestoreTarget(action.freeformRestoreTarget);
      }
      if (action.freeformReviewResult !== undefined) {
        setFreeformReviewResult(action.freeformReviewResult);
      }
      if (action.sourceWorkspaceRestoreKey !== undefined) {
        setSourceWorkspaceRestoreKey(action.sourceWorkspaceRestoreKey);
      }
      if (action.comparisonResult !== undefined) {
        setComparisonResult(action.comparisonResult);
      }
      if (action.activeTab !== undefined) {
        setActiveTab(action.activeTab);
      }
      if (action.progressDismissed !== undefined) {
        setProgressDismissed(action.progressDismissed);
      }
    },
    [setProjectId],
  );

  const applyImportedBundle = useCallback(
    async (restored: AICheckShareBundleRestoreResult) => {
      const inferredInput = projectDataInputFromCachedResult(restored.result);
      const input: ProjectDataRunInput | null = inferredInput
        ? {
            ...inferredInput,
            mergeCheckCache:
              restored.mergeCheckCache ?? inferredInput.mergeCheckCache,
          }
        : null;
      applyWorkspaceSelection({
        projectId: restored.projectId,
        selectedLanguage: restored.language,
        runLanguage: restored.language,
        persistLanguage: {
          projectId: restored.projectId,
          language: restored.language,
        },
        activeProjectDataInput: input,
        freeformRestoreTarget: restored.freeformReviewResult
          ? {
              templateId: restored.freeformReviewResult.templateId,
              prompt: restored.freeformReviewResult.prompt,
              cacheKey: `${restored.projectId}:${restored.freeformReviewResult.language}:${restored.freeformReviewResult.templateId}:${restored.freeformReviewResult.promptHash}:${restored.freeformReviewResult.cachedAt}`,
            }
          : null,
        freeformReviewResult: restored.freeformReviewResult?.result ?? null,
        sourceWorkspaceRestoreKey: sourceWorkspaceRestoreKeyFor(
          input,
          restored.result,
        ),
        comparisonResult: input?.comparison ?? null,
        activeTab: "basic",
        progressDismissed: false,
      });
      await loadCachedResult(restored.projectId, restored.language);
    },
    [applyWorkspaceSelection, loadCachedResult],
  );
  const {
    pendingShareBundleImport,
    isShareBundleImporting,
    handleImportShareBundle,
    confirmImportShareBundle,
    cancelImportShareBundle,
  } = useShareBundleImport({
    setShareBundleStatus,
    onApplyImportedBundle: applyImportedBundle,
  });
  const setFocusedSummaryFindingIndex = useAICheckStore(
    (state) => state.setFocusedSummaryFindingIndex,
  );
  const setFocusedCheckId = useAICheckStore((state) => state.setFocusedCheckId);
  const setFocusedEvidenceTarget = useAICheckStore(
    (state) => state.setFocusedEvidenceTarget,
  );
  const setHighlightCheckId = useAICheckStore(
    (state) => state.setHighlightCheckId,
  );
  const setHighlightEvidenceTarget = useAICheckStore(
    (state) => state.setHighlightEvidenceTarget,
  );
  const setSummaryReturnTarget = useAICheckStore(
    (state) => state.setSummaryReturnTarget,
  );
  const focusedSummaryFindingIndexValue = useAICheckStore(
    (state) => state.focusedSummaryFindingIndex,
  );
  const focusedCheckIdValue = useAICheckStore((state) => state.focusedCheckId);
  const focusedEvidenceTargetValue = useAICheckStore(
    (state) => state.focusedEvidenceTarget,
  );
  const [serverProjects, setServerProjects] = useState<ProjectRecord[]>([]);
  // Deep-link state parsed from the URL, applied once the result is displayed.
  const pendingUrlFocusRef = useRef<{
    projectId: number;
    tab: ActiveTab | null;
    checkId: string | null;
    evidenceIndex: number | null;
    findingIndex: number | null;
  } | null>(null);
  // URL write-back stays off until the initial deep link has been consumed,
  // so defaults don't overwrite the shared URL before it is applied.
  const urlSyncEnabledRef = useRef(false);
  const selectedProjectId = useMemo(() => {
    const parsed = Number(projectId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [projectId]);

  // Restore per-project language when projectId changes
  useEffect(() => {
    if (!projectId) return;
    // 観測中は実行の言語が正。ここで localStorage の値に戻すと、
    // JA で実行しているのに前回の EN 表示へ引き戻される。
    if (observeRunId) return;
    const stored = readStoredLang(projectId);
    if (stored) {
      setSelectedLanguageState(stored);
      setRunLanguage(stored);
    }
    // intentionally not including readStoredLang — it's stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, observeRunId]);

  // effect の依存から外すための最新値保持（下の useEffect のコメント参照）
  const observeRunProgressRef = useRef(observeRunProgress);
  observeRunProgressRef.current = observeRunProgress;

  // 親が「この実行を見ろ」と言ってきたら、その実行を runId で観測する。
  // 実行の言語に表示も合わせる — 合わせないと、JA で実行したのに前回の
  // EN の結果が並んだままになる（localStorage の案件別言語が復元される）。
  //
  // **親の prop だけを見て案件を決める。** ストアの projectId を優先すると
  // 未設定→設定で一度張り直しになり、リプレイが最初からやり直しになる。
  const observedProjectId = initialProjectId || Number(projectId) || 0;
  const observedLanguage = observeLanguage ?? selectedLanguage;

  // 表示言語を実行の言語に合わせる。観測そのものとは別の関心なので分ける。
  useEffect(() => {
    if (!observeRunId || !observedProjectId) return;
    setSelectedLanguageState(observedLanguage);
    setRunLanguage(observedLanguage);
    // 実行が終わって復元効果が動いたときも同じ言語になるよう永続化する
    writeStoredLang(observedProjectId, observedLanguage);
  }, [observeRunId, observedProjectId, observedLanguage]);

  // 畳むときだけ猶予を置く理由は useGracefulObservation を参照。
  useGracefulObservation({
    runId: observeRunId && observedProjectId ? observeRunId : null,
    scopeKey: `${observedProjectId}|${observedLanguage}`,
    // **関数を依存に入れてはいけない。** observeRunProgress は store に依存した
    // useCallback で、store は状態が変わるたび別参照になる。そして観測開始は
    // setReconnecting で状態を変える。依存に入れると
    // 「観測開始 → 状態変化 → 関数の再生成 → effect 再実行 → abort → 観測開始」
    // が回り続け、画面が点滅しサーバへ fromSeq=0 の接続が殺到する。
    // useGracefulObservation は start を ref 経由で読むので identity は問わない。
    start: (signal) =>
      observeRunProgressRef.current(
        observeRunId as string,
        observedProjectId,
        observedLanguage,
        signal,
      ),
    onError: (error) =>
      console.error("[AICheckWorkspace] observation failed:", error),
  });

  // Restore runLanguage from sessionStorage on mount (reconnect path)
  // biome-ignore lint/correctness/useExhaustiveDependencies: マウント時に一度だけ判定する。観測開始後に再実行すると表示言語を前回値へ戻してしまう
  useEffect(() => {
    // 観測中は実行の言語が正。ここで前回値に戻すと表示だけ別言語になる。
    if (observeRunId) return;
    try {
      const stored = sessionStorage.getItem("aicheck:runLanguage");
      if (stored === "en" || stored === "ja") {
        setRunLanguage(stored);
        setSelectedLanguageState(stored);
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, []);

  const { pushTraceabilityHistory } = useTraceabilityHistory({ setActiveTab });

  const handleManualTabChange = useCallback(
    (tab: ActiveTab) => {
      if (tab === activeTab) return;
      setFocusedSummaryFindingIndex(null);
      setFocusedCheckId(null);
      setFocusedEvidenceTarget(null);
      setHighlightCheckId(null);
      setHighlightEvidenceTarget(null);
      setSummaryReturnTarget(null);
      setActiveTab(tab);
      if (isTraceabilityTab(tab)) {
        pushTraceabilityHistory({ kind: "tab", tab });
      }
    },
    [
      activeTab,
      pushTraceabilityHistory,
      setFocusedCheckId,
      setFocusedEvidenceTarget,
      setFocusedSummaryFindingIndex,
      setHighlightCheckId,
      setHighlightEvidenceTarget,
      setSummaryReturnTarget,
    ],
  );

  const handleTraceabilityHistoryPush = useCallback(
    (state: TraceabilityHistoryState) => {
      pushTraceabilityHistory(state);
    },
    [pushTraceabilityHistory],
  );

  const handleReturnToSummary = useCallback(
    (findingIndex: number) => {
      if (typeof window !== "undefined") {
        const currentState = normalizeTraceabilityHistoryState(
          window.history.state?.[TRACEABILITY_HISTORY_KEY],
        );
        if (
          currentState &&
          (currentState.kind === "check" || currentState.kind === "evidence") &&
          currentState.returnFindingIndex === findingIndex
        ) {
          window.history.back();
          return;
        }
      }

      setActiveTab("basic");
      setSummaryReturnTarget(null);
      setFocusedSummaryFindingIndex(findingIndex);
      pushTraceabilityHistory({
        kind: "summary-finding",
        tab: "basic",
        summaryFindingIndex: findingIndex,
      });
    },
    [
      pushTraceabilityHistory,
      setFocusedSummaryFindingIndex,
      setSummaryReturnTarget,
    ],
  );

  // Fetch rate limit info on mount and when results complete
  useEffect(() => {
    let cancelled = false;
    async function fetchRateLimit() {
      try {
        const jwt = await authService.getIdToken();
        if (!jwt || cancelled) return;
        const res = await fetch(apiUrl("/api/aicheck/rate-limit"), {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && data.rateLimitInfo) {
          setRateLimitInfo(data.rateLimitInfo);
        }
      } catch {
        // Silently ignore — rate limit info is informational only
      }
    }
    fetchRateLimit();
    return () => {
      cancelled = true;
    };
  }, []);

  const isRunning = ![
    "idle",
    "completed",
    "aborted",
    "error",
    "cancelled",
    "disconnected",
  ].includes(runProgress.status);

  const checkpointDisplayResult = useMemo(() => {
    const pid = Number(projectId);
    if (!Number.isFinite(pid) || pid <= 0) return null;

    const savedCheckpoint = checkpoint.getCheckpoint(
      pid,
      runLanguage,
    ).checkpoint;
    if (!savedCheckpoint?.projectDataSnapshot) return null;

    const isActiveCheckpoint =
      isRunning ||
      runProgress.status === "aborted" ||
      runProgress.status === "disconnected";
    const fallbackStatus =
      savedCheckpoint.lastKnownStatus === "cancelled"
        ? "cancelled"
        : "completed";
    const result = buildRunResultFromCheckpoint(savedCheckpoint, {
      startedAt: savedCheckpoint.startedAt,
      completedAt: isActiveCheckpoint ? "" : savedCheckpoint.updatedAt,
      status: fallbackStatus,
    });

    return isActiveCheckpoint
      ? {
          ...result,
          completedAt: "",
        }
      : result;
  }, [checkpoint, isRunning, projectId, runLanguage, runProgress.status]);

  // Derive displayResult: use final result when available, otherwise build
  // a synthetic AICheckRunResult from checkpoint / partial data during execution.
  const displayResult: AICheckRunResult | null = useMemo(() => {
    if (resultMatchesProjectId(currentResult, selectedProjectId)) {
      return currentResult;
    }
    if (resultMatchesProjectId(checkpointDisplayResult, selectedProjectId)) {
      return checkpointDisplayResult;
    }
    if (
      partialRunData?.projectData &&
      partialRunData.projectId === selectedProjectId
    ) {
      return {
        projectId: partialRunData.projectId,
        startedAt: partialRunData.startedAt,
        completedAt: "",
        status: "completed" as const,
        language: runLanguage,
        projectData: partialRunData.projectData,
        projectDataSource: partialRunData.projectDataSource,
        aiResults: partialRunData.aiResults,
        consistencyResults: partialRunData.consistencyResults,
        customerProfileResult: partialRunData.customerProfileResult,
        totalTokensUsed: { prompt: 0, completion: 0, total: 0 },
      };
    }
    if (resultMatchesProjectId(previousResult, selectedProjectId)) {
      return previousResult;
    }
    return null;
  }, [
    checkpointDisplayResult,
    currentResult,
    partialRunData,
    previousResult,
    runLanguage,
    selectedProjectId,
  ]);

  const freeformProjectDataInput = useMemo<ProjectDataRunInput | null>(() => {
    if (activeProjectDataInput?.projectData) return activeProjectDataInput;
    if (!displayResult?.projectData) return null;
    return {
      projectData: displayResult.projectData,
      source: displayResult.projectDataSource,
      sourceLabel:
        displayResult.projectDataSource?.label ??
        `Project #${displayResult.projectId}`,
      seed: projectDataSourceSeed(
        displayResult.projectDataSource,
        String(displayResult.projectId),
      ),
      comparison: displayResult.projectDataComparison
        ? (typedProjectDataComparison<
            ProjectDataComparisonResult["findings"][number]
          >(displayResult.projectDataComparison) ?? undefined)
        : undefined,
    };
  }, [activeProjectDataInput, displayResult]);
  const freeformProjectData = freeformProjectDataInput?.projectData ?? null;
  const shareBundleResult = useMemo<AICheckRunResult | null>(() => {
    if (displayResult?.completedAt) return displayResult;
    if (!activeProjectDataInput?.projectData) return null;
    const projectDataId =
      selectedProjectId ??
      projectIdFromProjectDataSource(
        activeProjectDataInput.source,
        activeProjectDataInput.seed || activeProjectDataInput.sourceLabel || "",
      );
    return buildProjectDataOnlyRunResult({
      projectId: projectDataId,
      language: selectedLanguage,
      input: activeProjectDataInput,
      comparison: comparisonResult,
    });
  }, [
    activeProjectDataInput,
    comparisonResult,
    displayResult,
    selectedLanguage,
    selectedProjectId,
  ]);
  const overviewCheckResult =
    displayResult && String(displayResult.projectId) === String(projectId)
      ? displayResult
      : null;
  const overviewProjectData =
    projectDataOverviewMode === "freeform"
      ? (freeformProjectData ?? overviewCheckResult?.projectData ?? null)
      : (overviewCheckResult?.projectData ?? freeformProjectData ?? null);
  const overviewSourceLabel =
    projectDataOverviewMode === "freeform"
      ? (freeformProjectDataInput?.source?.label ??
        overviewCheckResult?.projectDataSource?.label ??
        null)
      : (overviewCheckResult?.projectDataSource?.label ??
        freeformProjectDataInput?.source?.label ??
        null);

  const isStale =
    isRunning &&
    displayResult === previousResult &&
    !resultMatchesProjectId(currentResult, selectedProjectId) &&
    !resultMatchesProjectId(checkpointDisplayResult, selectedProjectId) &&
    !(
      partialRunData?.projectData &&
      partialRunData.projectId === selectedProjectId
    );

  const handleComparisonResult = useCallback(
    (result: ProjectDataComparisonResult | null) => {
      setComparisonResult(result);
      if (result) {
        setActiveTab("comparison");
      } else if (activeTab === "comparison") {
        setActiveTab("basic");
      }
    },
    [activeTab],
  );

  const handleOpenProjectDataOverview = useCallback(
    (
      focusSource: string | null = null,
      mode: ProjectDataOverviewResultMode = "check",
    ) => {
      const popupWindow = openProjectDataOverviewPopupWindow();
      projectDataOverviewWindowRef.current = popupWindow;
      setProjectDataOverviewWindow(popupWindow);
      setProjectDataOverviewFocusSource(focusSource);
      setProjectDataOverviewMode(mode);
      setProjectDataOverviewFocusKey((current) => current + 1);
      setShowProjectDataOverview(true);
    },
    [],
  );

  const handleCloseProjectDataOverview = useCallback(() => {
    const popupWindow = projectDataOverviewWindowRef.current;
    projectDataOverviewWindowRef.current = null;
    setShowProjectDataOverview(false);
    setProjectDataOverviewWindow(null);
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }
  }, []);

  const handleOpenCheckFromProjectDataOverview = useCallback(
    (issue: ProjectDataOverviewIssue) => {
      if (issue.sourceKind === "freeform") {
        setActiveTab("freeform");
        window.focus();
        return;
      }

      const tab = categoryToTraceabilityTab(issue.category);
      setActiveTab(tab);
      setFocusedSummaryFindingIndex(null);
      setFocusedCheckId(null);
      setFocusedEvidenceTarget(null);
      setHighlightCheckId(null);
      setHighlightEvidenceTarget(null);
      setSummaryReturnTarget(null);

      if (
        issue.sourceKind === "ai" &&
        typeof issue.reference.evidenceIndex === "number"
      ) {
        setFocusedEvidenceTarget({
          checkId: issue.checkId,
          evidenceIndex: issue.reference.evidenceIndex,
        });
        pushTraceabilityHistory({
          kind: "evidence",
          tab,
          checkId: issue.checkId,
          evidenceIndex: issue.reference.evidenceIndex,
        });
      } else {
        setFocusedCheckId(issue.checkId);
        pushTraceabilityHistory({
          kind: "check",
          tab,
          checkId: issue.checkId,
        });
      }

      window.focus();
    },
    [
      pushTraceabilityHistory,
      setFocusedCheckId,
      setFocusedEvidenceTarget,
      setFocusedSummaryFindingIndex,
      setHighlightCheckId,
      setHighlightEvidenceTarget,
      setSummaryReturnTarget,
    ],
  );

  const projectIdForProjectDataInput = useCallback(
    (input: ProjectDataRunInput): number => {
      return projectIdFromProjectDataSource(
        input.source,
        input.seed || input.sourceLabel,
      );
    },
    [],
  );

  const restoreCachedCheckResultForInput = useCallback(
    async (params: {
      id: number;
      language: OutputLanguage;
      input: ProjectDataRunInput;
      allowAnyLanguage?: boolean;
    }): Promise<{ loaded: boolean; language: OutputLanguage }> => {
      let effectiveLang = params.language;
      let { loaded } = await loadCachedResult(params.id, effectiveLang);
      if (loaded || !params.input.source) {
        return { loaded, language: effectiveLang };
      }

      const sourceCachedResult =
        findResultByProjectDataSource(params.input.source, effectiveLang) ??
        (params.allowAnyLanguage
          ? findResultByProjectDataSource(params.input.source)
          : null);
      if (!sourceCachedResult) {
        return { loaded: false, language: effectiveLang };
      }

      effectiveLang = sourceCachedResult.language;
      saveResult(
        sourceCachedResult.projectId === params.id
          ? sourceCachedResult
          : { ...sourceCachedResult, projectId: params.id },
      );
      loaded = (await loadCachedResult(params.id, effectiveLang)).loaded;
      return { loaded, language: effectiveLang };
    },
    [findResultByProjectDataSource, loadCachedResult, saveResult],
  );

  const handleLoadCachedProjectData = useCallback(
    async (input: ProjectDataRunInput) => {
      const id = projectIdForProjectDataInput(input);
      const storedLang = readStoredLang(id);
      let effectiveLang = storedLang ?? selectedLanguage;
      reset();
      applyWorkspaceSelection({
        projectId: id,
        selectedLanguage: effectiveLang,
        runLanguage: effectiveLang,
        persistLanguage: { projectId: id, language: effectiveLang },
        activeProjectDataInput: input,
        freeformRestoreTarget: null,
        freeformReviewResult: null,
        sourceWorkspaceRestoreKey: null,
        comparisonResult: input.comparison ?? null,
        progressDismissed: false,
      });
      const restoredCheck = await restoreCachedCheckResultForInput({
        id,
        language: effectiveLang,
        input,
        allowAnyLanguage: !storedLang,
      });
      const loaded = restoredCheck.loaded;
      let restoredFreeform = false;
      if (restoredCheck.language !== effectiveLang) {
        effectiveLang = restoredCheck.language;
        applyWorkspaceSelection({
          selectedLanguage: effectiveLang,
          runLanguage: effectiveLang,
          persistLanguage: { projectId: id, language: effectiveLang },
        });
      }
      if (!loaded && input.source) {
        const freeformCached =
          readLatestFreeformReviewResultCache({
            projectId: id,
            language: effectiveLang,
            source: input.source,
          }) ??
          (!storedLang
            ? readLatestFreeformReviewResultCache({
                projectId: id,
                source: input.source,
              })
            : null);
        if (freeformCached) {
          effectiveLang = freeformCached.language;
          applyWorkspaceSelection({
            selectedLanguage: effectiveLang,
            runLanguage: effectiveLang,
            persistLanguage: { projectId: id, language: effectiveLang },
            freeformRestoreTarget: {
              templateId: freeformCached.templateId,
              prompt: freeformCached.prompt,
              cacheKey: `${id}:${freeformCached.language}:${freeformCached.templateId}:${freeformCached.promptHash}:${freeformCached.cachedAt}`,
            },
            freeformReviewResult: freeformCached.result,
            activeTab: "freeform",
          });
          restoredFreeform = true;
        }
      }
      if (loaded) {
        const cached = getResult(id, effectiveLang).result;
        const cachedInput = cached
          ? projectDataInputFromCachedResult(cached)
          : null;
        applyWorkspaceSelection({
          // 元実装は cachedInput が truthy のときだけ activeProjectDataInput を
          // 更新していた（null では上書きしない）。その差を維持する。
          ...(cachedInput ? { activeProjectDataInput: cachedInput } : {}),
          comparisonResult: cachedInput?.comparison ?? null,
          activeTab: cachedInput?.comparison ? "comparison" : "basic",
        });
      } else if (input.projectData && !restoredFreeform) {
        applyWorkspaceSelection({
          activeTab: input.comparison ? "comparison" : "freeform",
        });
      }
    },
    [
      applyWorkspaceSelection,
      getResult,
      projectIdForProjectDataInput,
      reset,
      restoreCachedCheckResultForInput,
      selectedLanguage,
    ],
  );

  const handleRunProjectData = useCallback(
    (input: ProjectDataRunInput) => {
      const id = projectIdForProjectDataInput(input);
      // 注: selectedLanguage は「現在の選択値」をそのまま実行言語に使うため
      // 更新不要（恒等代入になる）。キーを渡さないことでそれを明示している。
      applyWorkspaceSelection({
        projectId: id,
        runLanguage: selectedLanguage,
        persistLanguage: { projectId: id, language: selectedLanguage },
        activeProjectDataInput: input,
        freeformRestoreTarget: null,
        freeformReviewResult: null,
        sourceWorkspaceRestoreKey: null,
        // 比較なし入力の実行時に前入力の「比較」タブが残留しないよう、
        // 入力自身の比較で置換（なければ明示クリア）する
        comparisonResult: input.comparison ?? null,
        activeTab: "basic",
        progressDismissed: false,
      });
      runCheck(id, selectedLanguage, {
        mode: "single-project-data",
        conversionId: input.conversionId,
        projectData: input.projectData,
        projectDataSource: input.source,
        projectDataComparison: input.comparison,
        mergeCheckCache: input.mergeCheckCache,
      });
    },
    [
      applyWorkspaceSelection,
      projectIdForProjectDataInput,
      runCheck,
      selectedLanguage,
    ],
  );

  const handleFreeformReviewProjectData = useCallback(
    (input: ProjectDataRunInput) => {
      if (!input.projectData) return;
      const id = projectIdForProjectDataInput(input);
      reset();
      applyWorkspaceSelection({
        projectId: id,
        selectedLanguage: selectedLanguage,
        runLanguage: selectedLanguage,
        persistLanguage: { projectId: id, language: selectedLanguage },
        activeProjectDataInput: input,
        freeformRestoreTarget: null,
        freeformReviewResult: null,
        sourceWorkspaceRestoreKey: null,
        comparisonResult: input.comparison ?? null,
        activeTab: "freeform",
        progressDismissed: false,
      });
    },
    [
      applyWorkspaceSelection,
      projectIdForProjectDataInput,
      reset,
      selectedLanguage,
    ],
  );

  // ── CRM取り込みダイアログ（AIチェック無しでも取り込めるように）───────────────
  // project variant で結果が無い案件を開いたら「(a)EN/JA選択してチェック実行 /
  // (b)旅程のみ取り込み(=チェックせずデータだけ取り込み freeform 可能に)」を選ばせる。
  const [ingestDialogOpen, setIngestDialogOpen] = useState(false);
  const [ingestBusy, setIngestBusy] = useState(false);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const ingestPromptedRef = useRef(false);

  // CRM からデータだけ取得して ProjectDataRunInput を組む（LLM実行なし）。
  const fetchCrmProjectDataInput = useCallback(
    async (id: number): Promise<ProjectDataRunInput> => {
      const token = await authService.getIdToken();
      if (!token) throw new Error("未認証です。再度サインインしてください。");
      const res = await fetch(
        apiUrl(`/api/aicheck/project-data/${encodeURIComponent(String(id))}`),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      const projectData = (await res.json()) as AggregatedProjectData;
      const source: ProjectDataSourceDescriptor = {
        sourceId: `crm-${id}`,
        label: `CRM Project #${id}`,
        kind: "crm-api",
        trustLevel: "authoritative",
        dataDigest: `crm-project-${id}`,
        origin: { crmProjectId: id, generatedAt: projectData.fetchedAt },
      };
      return {
        projectData,
        source,
        sourceLabel: source.label,
        seed: `crm-${id}`,
      };
    },
    [],
  );

  // (a) 選択言語で通常の CRM チェックを実行。
  const handleIngestRunCheck = useCallback(() => {
    const id = Number(projectId);
    if (!(id > 0) || isRunning) return;
    writeStoredLang(id, selectedLanguage);
    setRunLanguage(selectedLanguage);
    setProgressDismissed(false);
    setIngestDialogOpen(false);
    runCheck(id, selectedLanguage);
  }, [projectId, isRunning, selectedLanguage, runCheck]);

  // (b) チェックせず旅程データだけ取り込み、freeform で使える状態にする。
  const handleIngestItineraryOnly = useCallback(async () => {
    const id = Number(projectId);
    if (!(id > 0)) return;
    setIngestBusy(true);
    setIngestError(null);
    try {
      const input = await fetchCrmProjectDataInput(id);
      handleFreeformReviewProjectData(input);
      setIngestDialogOpen(false);
    } catch (error) {
      setIngestError(
        error instanceof Error ? error.message : "取り込みに失敗しました。",
      );
    } finally {
      setIngestBusy(false);
    }
  }, [projectId, fetchCrmProjectDataInput, handleFreeformReviewProjectData]);

  // コミット済みサーバ結果が無い CRM 案件（page が promptIngestOnMount=true を渡す）を
  // 開いたら、取り込みダイアログを1度だけ開く。
  //  - 開くトリガは「権威シグナル(=prop)」。settled な client state（cache 復元等）に依存させない。
  //  - ただしマウント直後は reconnect がまだ isRunning を確定していない。実行中の案件を
  //    リロードしたときにダイアログが誤って開くのを防ぐため、短い猶予後に判定する。
  //  - 実行中 / reconnect 中は開かない（進行中の作業を優先）。開いた後に実行が始まったら閉じる。
  const [ingestArmed, setIngestArmed] = useState(false);
  useEffect(() => {
    if (!promptIngestOnMount) return;
    // reconnect が isRunning を立てる猶予を置いてから判定を有効化する。
    const timer = setTimeout(() => setIngestArmed(true), 1200);
    return () => clearTimeout(timer);
  }, [promptIngestOnMount]);

  useEffect(() => {
    if (!ingestArmed || ingestPromptedRef.current) return;
    if (isRunning) return; // ライブ実行中 / reconnect 中は譲る（ダイアログを出さない）
    // **中断・失敗の直後には出さない。** 利用者はいま実行を止めたばかりで、
    // 画面には "Check Aborted." が出ている。その上に取り込みダイアログを
    // かぶせると、止めた操作が失敗したように見える。出すのは「まだ何も
    // 始まっていない」ときだけ。
    if (runProgress.status !== "idle") return;
    ingestPromptedRef.current = true;
    setIngestError(null);
    setIngestDialogOpen(true);
  }, [ingestArmed, isRunning, runProgress.status]);

  // 実行が始まった / reconnect した瞬間に、自動オープンのダイアログは閉じて進捗に譲る。
  useEffect(() => {
    if (isRunning) setIngestDialogOpen(false);
  }, [isRunning]);

  const handleLoadFreeformCachedProject = useCallback(
    async (entry: FreeformReviewResultCacheEntry) => {
      const sourceCache = readProjectDataSourceCache(
        entry.source?.origin?.cacheKey,
      );
      const projectData = sourceCache?.projectData ?? entry.projectData;
      if (!projectData) {
        window.alert(
          "Cached Freeform Review result was found, but the source projectData is no longer available. Convert or load the source file again.",
        );
        return;
      }

      const source = entry.source ?? sourceCache?.source;
      const input: ProjectDataRunInput = {
        projectData,
        source,
        sourceLabel:
          source?.label ||
          entry.stage ||
          entry.templateName ||
          "Freeform Review",
        seed: projectDataSourceSeed(source, String(entry.projectId)),
      };
      const id = source ? projectIdForProjectDataInput(input) : entry.projectId;
      reset();
      const restoredCheck = await restoreCachedCheckResultForInput({
        id,
        language: entry.language,
        input,
      });
      const cachedCheck = restoredCheck.loaded
        ? getResult(id, restoredCheck.language).result
        : null;
      const cachedInput = cachedCheck
        ? projectDataInputFromCachedResult(cachedCheck)
        : null;
      const activeInput = cachedInput ?? input;

      applyWorkspaceSelection({
        projectId: id,
        selectedLanguage: restoredCheck.language,
        runLanguage: restoredCheck.language,
        persistLanguage: { projectId: id, language: restoredCheck.language },
        activeProjectDataInput: activeInput,
        freeformRestoreTarget: {
          templateId: entry.templateId,
          prompt: entry.prompt,
          cacheKey: `${id}:${restoredCheck.language}:${entry.templateId}:${entry.promptHash}:${entry.cachedAt}`,
        },
        freeformReviewResult: entry.result,
        sourceWorkspaceRestoreKey: sourceWorkspaceRestoreKeyFor(
          activeInput,
          cachedCheck,
        ),
        comparisonResult: activeInput.comparison ?? null,
        activeTab: "freeform",
        progressDismissed: false,
      });
    },
    [
      applyWorkspaceSelection,
      getResult,
      projectIdForProjectDataInput,
      reset,
      restoreCachedCheckResultForInput,
    ],
  );

  const handleLoadCachedMergeCheck = useCallback(
    async (entry: MergeCheckResultCacheEntry) => {
      const input: ProjectDataRunInput = {
        projectData: entry.result.projectData,
        source: entry.result.projectDataSource,
        sourceLabel: entry.metadata.sourceLabel,
        seed: entry.metadata.seed,
        comparison:
          comparisonFromCachedResult(entry.result, entry) ?? undefined,
        mergeCheckCache: entry.metadata,
      };
      const result =
        input.comparison && !entry.result.projectDataComparison
          ? { ...entry.result, projectDataComparison: input.comparison }
          : entry.result;
      saveResult(result);
      applyWorkspaceSelection({
        projectId: result.projectId,
        selectedLanguage: result.language,
        runLanguage: result.language,
        persistLanguage: {
          projectId: result.projectId,
          language: result.language,
        },
        activeProjectDataInput: input,
        freeformRestoreTarget: null,
        freeformReviewResult: null,
        sourceWorkspaceRestoreKey: sourceWorkspaceRestoreKeyFor(input, result),
        comparisonResult: input.comparison ?? null,
        activeTab: input.comparison ? "comparison" : "basic",
        progressDismissed: false,
      });
      await loadCachedResult(result.projectId, result.language);
    },
    [applyWorkspaceSelection, loadCachedResult, saveResult],
  );

  const handleDownloadShareBundle = useCallback(async () => {
    if (!shareBundleResult) return;
    setShareBundleStatus("Preparing share bundle...");
    try {
      const resultForBundle =
        comparisonResult && !shareBundleResult.projectDataComparison
          ? { ...shareBundleResult, projectDataComparison: comparisonResult }
          : shareBundleResult;
      const activeSource =
        activeProjectDataInput &&
        isSameProjectDataSource(
          activeProjectDataInput.source,
          resultForBundle.projectDataSource,
        )
          ? {
              source: activeProjectDataInput.source,
              mergeCheckCache: activeProjectDataInput.mergeCheckCache,
            }
          : null;
      const bundle = await createAICheckShareBundle({
        result: resultForBundle,
        activeSource,
      });
      await downloadAICheckShareBundle(bundle);
      setShareBundleStatus("Share bundle downloaded.");
    } catch (error) {
      setShareBundleStatus(
        error instanceof Error
          ? error.message
          : "Share bundle download failed.",
      );
    }
  }, [activeProjectDataInput, comparisonResult, shareBundleResult]);

  // Handle query parameter on load / change
  const handleLoadProject = useCallback(
    async (id: number, lang?: OutputLanguage, opts?: { autoRun?: boolean }) => {
      // Prefer: explicit lang arg → stored per-project lang → current selectedLanguage
      const effectiveLang = lang ?? readStoredLang(id) ?? selectedLanguage;
      applyWorkspaceSelection({
        projectId: id,
        selectedLanguage: effectiveLang,
        runLanguage: effectiveLang,
        persistLanguage: { projectId: id, language: effectiveLang },
        activeProjectDataInput: null,
        freeformRestoreTarget: null,
        // 前案件の freeform 回答が残るとデータ全体表示(freeform モード)に
        // 別案件の指摘が混在するため、案件を開く時点で明示クリアする
        freeformReviewResult: null,
        sourceWorkspaceRestoreKey: null,
        comparisonResult: null,
        progressDismissed: false,
      });
      // **観測中はここで別経路を走らせない。** observe が実行中の状態を
      // 書き込んでいる最中に、キャッシュ読み込みや reset が上書きすると
      // 進捗が消えたり古い結果に戻ったりする。実行の表示は observe が持つ。
      if (observeRunIdRef.current) return;

      // **親が観測を持つなら、繋ぎ直しも親の指示を待つ。** 上の
      // `observeRunIdRef.current` は、親がサーバに問い合わせている間はまだ
      // null で、「実行が無い」と区別できない。ここで自前で繋ぐと同じ実行に
      // 観測が二本張られる。キャッシュ読み込みは進めてよい（実行があれば
      // 親の runId で観測が始まり、そちらが表示を持つ）。
      const reconnected = parentOwnsObservation
        ? false
        : await reconnectRun(id, effectiveLang);
      if (reconnected) return;

      const checkpointShown = showCheckpointAsAborted(id, effectiveLang);
      if (checkpointShown) {
        return;
      }

      const { loaded, staleReason } = await loadCachedResult(id, effectiveLang);
      if (loaded) {
        const cached = getResult(id, effectiveLang).result;
        const input = cached ? projectDataInputFromCachedResult(cached) : null;
        applyWorkspaceSelection({
          activeProjectDataInput: input,
          freeformRestoreTarget: null,
          sourceWorkspaceRestoreKey: sourceWorkspaceRestoreKeyFor(
            input,
            cached,
          ),
          comparisonResult: input?.comparison ?? null,
          activeTab: input?.comparison ? "comparison" : "basic",
        });
        return;
      }
      if (!loaded) {
        if (staleReason) {
          clearAllResults(id);
        }
        // 何もロードできなかった = この案件に対する実行中/再接続可能な run も保存結果も
        // 無い。前に見ていた案件からグローバル store に残った run 状態（status 等）を
        // クリアして fresh 案件として扱う。これをしないと、前案件の非終端 status が
        // isRunning=true として残り、取り込みダイアログが誤って抑止される。
        // ※reconnect / checkpoint / cache 成功時は上で return 済みなので、本当に実行中の
        //   案件がここでリセットされることはない。
        reset();
        // In the embedded (project) variant, never fall back to prompting for a
        // new run — the parent page owns the run trigger.
        if (opts?.autoRun === false) return;
        // No stored result on the server for this project+language. Running a
        // check costs LLM execution — always confirm instead of auto-running.
        const confirmed = window.confirm(
          `プロジェクト #${id}（${effectiveLang.toUpperCase()}）の保存された結果がありません。\n新しくAIチェックを実行しますか？（LLM実行が発生します）`,
        );
        if (confirmed) {
          runCheck(id, effectiveLang);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applyWorkspaceSelection,
      loadCachedResult,
      getResult,
      runCheck,
      clearAllResults,
      reconnectRun,
      parentOwnsObservation,
      showCheckpointAsAborted,
      selectedLanguage,
      reset,
    ],
  );

  // Language switch: persist to localStorage, try to load cached result for new language,
  // and update runLanguage if a cached result is available.
  const handleLanguageSwitch = useCallback(
    async (lang: OutputLanguage) => {
      // freeform 回答は言語固有のため、切替時点で旧言語の回答をクリアする
      // （残すとデータ全体表示の freeform モードに旧言語の指摘が混在する）
      applyWorkspaceSelection({
        selectedLanguage: lang,
        freeformReviewResult: null,
      });
      const pid = Number(projectId);
      if (pid > 0) {
        writeStoredLang(pid, lang);
        const { loaded } = await loadCachedResult(pid, lang);
        if (loaded) {
          const cached = getResult(pid, lang).result;
          const input = cached
            ? projectDataInputFromCachedResult(cached)
            : null;
          applyWorkspaceSelection({
            runLanguage: lang,
            activeProjectDataInput: input,
            freeformRestoreTarget: null,
            sourceWorkspaceRestoreKey: sourceWorkspaceRestoreKeyFor(
              input,
              cached,
            ),
            comparisonResult: input?.comparison ?? null,
            activeTab: input?.comparison ? "comparison" : "basic",
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applyWorkspaceSelection, projectId, loadCachedResult, getResult],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: observeRunId / observeLanguage は「どの言語で開くか」の初期判断にだけ使う。依存に入れると実行の開始・終了のたびに案件の読み込みが走り直す
  useEffect(() => {
    const idParam =
      variant === "project"
        ? initialProjectId
          ? String(initialProjectId)
          : null
        : searchParams.get("id");
    if (!idParam) {
      urlSyncEnabledRef.current = true;
      return;
    }
    if (idParam === processedIdRef.current) return;

    const id = Number(idParam);
    if (id <= 0) return;

    processedIdRef.current = idParam;

    const langParam = searchParams.get("lang");
    // 観測中は実行の言語で開く。URL や保存値で開くと、進捗は流れているのに
    // 別言語のキャッシュを読み込んで表示だけ食い違う。
    const lang: OutputLanguage | undefined =
      (observeRunId ? (observeLanguage ?? undefined) : undefined) ??
      (langParam === "en" || langParam === "ja" ? langParam : undefined);

    const tabParam = searchParams.get("tab");
    const checkParam = searchParams.get("check");
    const evidenceParam = Number(searchParams.get("evidence"));
    const findingParam = Number(searchParams.get("finding"));
    const hasFocusParams =
      isActiveTab(tabParam) ||
      !!checkParam ||
      searchParams.get("finding") != null;
    pendingUrlFocusRef.current = hasFocusParams
      ? {
          projectId: id,
          tab: isActiveTab(tabParam) ? tabParam : null,
          checkId: checkParam || null,
          evidenceIndex: Number.isInteger(evidenceParam) ? evidenceParam : null,
          findingIndex: Number.isInteger(findingParam) ? findingParam : null,
        }
      : null;

    void handleLoadProject(id, lang, {
      autoRun: variant !== "project",
    }).finally(() => {
      urlSyncEnabledRef.current = true;
    });
  }, [searchParams, handleLoadProject, variant, initialProjectId]);

  // Apply URL deep-link focus (tab / check / evidence / summary finding) once
  // the loaded result is on screen.
  useEffect(() => {
    if (!displayResult) return;
    const pending = pendingUrlFocusRef.current;
    if (!pending) return;
    if (displayResult.projectId !== pending.projectId) return;
    pendingUrlFocusRef.current = null;
    // 案件ページ埋め込みでは内部の「差分・履歴」タブが無い（ページ側パネルに一本化）
    if (pending.tab && !(variant === "project" && pending.tab === "history")) {
      setActiveTab(pending.tab);
    }
    if (pending.checkId && pending.evidenceIndex != null) {
      setFocusedEvidenceTarget({
        checkId: pending.checkId,
        evidenceIndex: pending.evidenceIndex,
      });
    } else if (pending.checkId) {
      setFocusedCheckId(pending.checkId);
    } else if (pending.findingIndex != null) {
      setFocusedSummaryFindingIndex(pending.findingIndex);
    }
  }, [
    displayResult,
    variant,
    setFocusedCheckId,
    setFocusedEvidenceTarget,
    setFocusedSummaryFindingIndex,
  ]);

  // Keep the URL in sync with the visible screen (project / language / tab /
  // focused check result) so any state can be shared and reproduced by URL.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!urlSyncEnabledRef.current) return;
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const pid = Number(projectId);
    if (pid > 0) {
      // Embedded (project) variant: the project id lives in the parent page's
      // route, so never write (or clear) the `id` query parameter.
      if (variant !== "project") {
        params.set("id", String(pid));
      }
      params.set("lang", selectedLanguage);
      if (activeTab !== "basic") params.set("tab", activeTab);
      else params.delete("tab");
      if (focusedEvidenceTargetValue) {
        params.set("check", focusedEvidenceTargetValue.checkId);
        params.set(
          "evidence",
          String(focusedEvidenceTargetValue.evidenceIndex),
        );
      } else if (focusedCheckIdValue) {
        params.set("check", focusedCheckIdValue);
        params.delete("evidence");
      } else {
        params.delete("check");
        params.delete("evidence");
      }
      if (focusedSummaryFindingIndexValue != null) {
        params.set("finding", String(focusedSummaryFindingIndexValue));
      } else {
        params.delete("finding");
      }
      // The load effect keys on `id`; mark it processed so our own write-back
      // does not re-trigger a load.
      processedIdRef.current = String(pid);
    } else {
      for (const key of ["id", "lang", "tab", "check", "evidence", "finding"]) {
        if (variant === "project" && key === "id") continue;
        params.delete(key);
      }
    }
    const query = params.toString();
    const next = `${url.pathname}${query ? `?${query}` : ""}${url.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) {
      window.history.replaceState(window.history.state, "", next);
    }
  }, [
    projectId,
    selectedLanguage,
    activeTab,
    focusedCheckIdValue,
    focusedEvidenceTargetValue,
    focusedSummaryFindingIndexValue,
    variant,
  ]);

  // Server-side saved project list for the empty state (entry variant only).
  useEffect(() => {
    if (variant === "project") return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await authService.getIdToken();
        if (!token || cancelled) return;
        const res = await fetch(apiUrl("/api/aicheck/projects"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { projects?: ProjectRecord[] };
        if (!cancelled) setServerProjects(data.projects ?? []);
      } catch {
        // list is informational — ignore fetch errors
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [variant]);

  /**
   * 生きている実行へ繋ぎ直す。
   *
   * 親が観測を持つ構成では、案件スコープで探し直すより親の runId を直接
   * 観測する方が確実（案件単位のキーで登録された run は旧 cancel/reconnect
   * では見つからない）。**signal は必ず渡す。** 渡さない観測は案件・言語・
   * runId が変わってもアンマウントしても止まらず、次の観測と二本並ぶ。
   */
  const attachToLiveRun = useCallback(
    async (
      id: number,
      lang: OutputLanguage,
      signal?: AbortSignal,
    ): Promise<boolean> => {
      const parentRunId = parentOwnsObservation
        ? observeRunIdRef.current
        : null;
      if (!parentRunId) return reconnectRun(id, lang);
      const outcome = await observeRunProgressRef.current(
        parentRunId,
        id,
        observeLanguage ?? lang,
        signal,
      );
      // 繋がらなかった（既に消えた run）なら、呼び出し元の再開経路へ落とす。
      return outcome.finalized || outcome.aborted;
    },
    [parentOwnsObservation, observeLanguage, reconnectRun],
  );

  useEffect(() => {
    if (runProgress.status !== "disconnected") {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      return;
    }

    const id = Number(projectId);
    if (!Number.isFinite(id) || id <= 0) return;

    let cancelled = false;
    const maxAttempts = 5;
    // **繋ぎ直した観測も止められなければならない。** ここで張る観測は
    // 案件・言語・runId が変わっても、アンマウントしても、signal を渡さない
    // 限り生き続ける（reconnectRun は自分の AbortController を持つが、
    // observeRunProgress を直接呼ぶ経路は呼び出し側が渡すしかない）。
    // 止まらない観測は、次の観測と二本並ぶ。
    const controller = new AbortController();

    const schedule = () => {
      if (cancelled) return;
      const attempt = reconnectAttemptsRef.current;
      const delayMs = Math.min(1000 * 2 ** attempt, 10_000);
      reconnectTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        // ここは status が "disconnected"（＝生きた観測が無い）ときだけ動く
        // ので、繋ぎ直しても二重にはならない。
        const ok = await attachToLiveRun(id, runLanguage, controller.signal);
        if (cancelled) return;
        if (ok) {
          reconnectAttemptsRef.current = 0;
          reconnectTimerRef.current = null;
          return;
        }
        const resumed = await resumeFromCheckpoint(id, runLanguage);
        if (cancelled) return;
        if (resumed.resumed) {
          reconnectAttemptsRef.current = 0;
          reconnectTimerRef.current = null;
          return;
        }
        if (resumed.fallbackToFullRun && allowRunActions) {
          reconnectAttemptsRef.current = 0;
          reconnectTimerRef.current = null;
          runCheck(id, runLanguage);
          return;
        }
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current < maxAttempts) {
          schedule();
        } else {
          reconnectTimerRef.current = null;
        }
      }, delayMs);
    };

    schedule();

    return () => {
      cancelled = true;
      controller.abort();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [
    runProgress.status,
    projectId,
    runLanguage,
    attachToLiveRun,
    resumeFromCheckpoint,
    runCheck,
    allowRunActions,
  ]);

  const handleRefresh = () => {
    const id = Number(projectId);
    if (id > 0 && !isRunning) {
      setProgressDismissed(false);
      setRunLanguage(selectedLanguage);
      if (activeProjectDataInput) {
        runCheck(id, selectedLanguage, {
          mode: "single-project-data",
          conversionId: activeProjectDataInput.conversionId,
          projectData: activeProjectDataInput.projectData,
          projectDataSource: activeProjectDataInput.source,
          projectDataComparison: activeProjectDataInput.comparison,
          mergeCheckCache: activeProjectDataInput.mergeCheckCache,
        });
      } else {
        runCheck(id, selectedLanguage);
      }
    }
  };

  const retryFromFailure = useCallback(
    (
      targetProjectId: number,
      lang: OutputLanguage,
      failedStage: typeof runProgress.failedStage,
      failedChecks: typeof runProgress.failedChecks,
      skippedItems: typeof runProgress.skippedItems,
      plannedChecksCount: number,
      completedChecksCount: number,
    ): boolean => {
      const skippedAICheckIds = (skippedItems ?? [])
        .filter((i) => i.kind === "ai-check")
        .map((i) => i.id);

      if (failedStage === "compaction") {
        if (skippedAICheckIds.length === 0) {
          runCheck(targetProjectId, lang);
          return true;
        }
        runSelectedChecks(targetProjectId, lang, skippedAICheckIds, undefined, {
          skipProfile: false,
          skipCompaction: false,
          reevaluateConsistency: true,
        });
        return true;
      }

      if (failedStage === "profile") {
        if (skippedAICheckIds.length === 0) {
          runCheck(targetProjectId, lang);
          return true;
        }
        runSelectedChecks(targetProjectId, lang, skippedAICheckIds, undefined, {
          skipProfile: false,
          skipCompaction: true,
          reevaluateConsistency: true,
        });
        return true;
      }

      if (failedStage === "ai-checks" && failedChecks?.length) {
        const failedIds = [
          ...new Set(
            failedChecks.flatMap((f) =>
              (f.rerunCheckIds?.length ?? 0) > 0
                ? (f.rerunCheckIds ?? [])
                : [f.checkId],
            ),
          ),
        ];
        const downstreamSkippedIds = skippedAICheckIds.filter(
          (id) => !failedIds.includes(id),
        );
        const hasUnresolvedDownstream =
          plannedChecksCount > completedChecksCount &&
          downstreamSkippedIds.length === 0;
        if (failedIds.length === 0 || hasUnresolvedDownstream) {
          runCheck(targetProjectId, lang);
          return true;
        }
        runSelectedChecks(
          targetProjectId,
          lang,
          [...failedIds, ...downstreamSkippedIds],
          undefined,
          { skipProfile: true, skipCompaction: true },
        );
        return true;
      }

      return false;
    },
    [runSelectedChecks, runCheck],
  );

  const handleRetryFailed = useCallback(() => {
    const { failedStage, failedChecks } = runProgress;
    const retryBaseResult = currentResult ?? previousResult;
    if (!currentResult) {
      if (!isRunning && retryBaseResult) {
        if (
          retryFromFailure(
            retryBaseResult.projectId,
            runLanguage,
            failedStage,
            failedChecks,
            runProgress.skippedItems,
            runProgress.plannedChecks?.length ?? 0,
            runProgress.completedCheckNames?.length ?? 0,
          )
        ) {
          return;
        }
      }
      const queuedIds = [
        ...new Set((failedChecks ?? []).map((f) => f.checkId)),
      ];
      if (queuedIds.length > 0) {
        pendingRetryCheckIdsRef.current = [
          ...new Set([...pendingRetryCheckIdsRef.current, ...queuedIds]),
        ];
        window.alert(
          "Current run is still in progress. Retry was queued and will start automatically after this run finishes.",
        );
      }
      return;
    }
    setProgressDismissed(false);
    retryFromFailure(
      currentResult.projectId,
      runLanguage,
      failedStage,
      failedChecks,
      runProgress.skippedItems,
      runProgress.plannedChecks?.length ?? 0,
      runProgress.completedCheckNames?.length ?? 0,
    );
  }, [
    retryFromFailure,
    runProgress,
    currentResult,
    previousResult,
    isRunning,
    runLanguage,
  ]);

  const handleAdminNodeAction = useCallback(
    async (
      action: "rerun_from_node" | "toggle_abort_from_here" | "abort_node",
      nodeId: string,
    ) => {
      const pid = Number(projectId);
      if (!Number.isFinite(pid) || pid <= 0) return;
      if (
        (action === "toggle_abort_from_here" || action === "abort_node") &&
        !isRunning
      ) {
        window.alert("Abort controls can only be used while a run is active.");
        return;
      }

      const confirmed = window.confirm(
        action === "rerun_from_node"
          ? `Re-run from node "${nodeId}"?`
          : action === "abort_node"
            ? `Abort node "${nodeId}" and block its descendants?`
            : `Toggle abort-from-here for node "${nodeId}"?`,
      );
      if (!confirmed) return;

      const response =
        action === "rerun_from_node"
          ? await adminRerunFromNode(pid, runLanguage, nodeId)
          : action === "abort_node"
            ? await abortNode(pid, runLanguage, nodeId)
            : await toggleAbortFromHere(pid, runLanguage, nodeId);

      if (!response.ok) {
        window.alert(
          response.message ??
            `Admin action failed: ${response.reason ?? "unknown_error"}`,
        );
      }
    },
    [
      abortNode,
      adminRerunFromNode,
      isRunning,
      projectId,
      runLanguage,
      toggleAbortFromHere,
    ],
  );

  useEffect(() => {
    const isTerminal = [
      "completed",
      "error",
      "cancelled",
      "disconnected",
    ].includes(runProgress.status);
    if (!isTerminal) return;
    const retryBaseResult = currentResult ?? previousResult;
    if (!retryBaseResult) return;
    if (pendingRetryCheckIdsRef.current.length === 0) return;

    const queued = pendingRetryCheckIdsRef.current;
    pendingRetryCheckIdsRef.current = [];
    runSelectedChecks(
      retryBaseResult.projectId,
      runLanguage,
      queued,
      undefined,
      {
        skipProfile: true,
        skipCompaction: true,
      },
    );
  }, [
    runProgress.status,
    currentResult,
    previousResult,
    runLanguage,
    runSelectedChecks,
  ]);

  // Notify the parent once when a run transitions from active to a terminal
  // state ("disconnected" is excluded — the reconnect effect may resume it).
  const runWasActiveRef = useRef(false);
  const onRunFinishedRef = useRef(onRunFinished);
  useEffect(() => {
    onRunFinishedRef.current = onRunFinished;
  }, [onRunFinished]);
  useEffect(() => {
    if (isRunning) {
      // "reconnecting" 単独では実行と見なさない — 再接続試行が不成立でも
      // キャッシュ復元で completed に遷移するため、これを実行完了と誤認すると
      // 親の再取得 → 再マウント → 再接続試行の無限ループになる。
      if (runProgress.status !== "reconnecting") {
        runWasActiveRef.current = true;
      }
      return;
    }
    const isTerminal = ["completed", "aborted", "error", "cancelled"].includes(
      runProgress.status,
    );
    if (runWasActiveRef.current && isTerminal) {
      runWasActiveRef.current = false;
      onRunFinishedRef.current?.();
    }
  }, [isRunning, runProgress.status]);

  // Derive project info for display
  const projectInfo = useMemo(() => {
    const projectData =
      activeProjectDataInput?.projectData ?? displayResult?.projectData;
    if (!projectData?.project) return null;
    const p = projectData.project;
    const sourceLabel =
      activeProjectDataInput?.sourceLabel ??
      activeProjectDataInput?.source?.label ??
      displayResult?.projectDataSource?.label ??
      null;
    const id =
      typeof p.id === "number" && Number.isFinite(p.id)
        ? p.id
        : (selectedProjectId ?? displayResult?.projectId ?? 0);
    const displayName = projectTitleFromProjectData(
      projectData,
      id,
      sourceLabel,
    );
    return {
      id,
      tourCode: p.tour_code,
      displayName,
      startDate: p.travel_start_date,
      endDate: p.travel_end_date,
      travelersCount: p.travelers_count,
    };
  }, [activeProjectDataInput, displayResult, selectedProjectId]);

  // Count issues per category for tab badges (excluding dismissed)
  const tabCounts = useMemo(() => {
    const empty = { fail: 0, warning: 0 };
    if (!displayResult)
      return { basic: empty, review: empty, reminders: empty };

    const dr = displayResult;
    function countCategory(category: string) {
      let fail = 0;
      let warning = 0;
      for (const r of dr.aiResults) {
        if (r.category !== category || isDismissed(r.checkId)) continue;
        if (r.status === "fail" || r.status === "error") fail++;
        else if (r.status === "warning") warning++;
      }
      for (const r of dr.consistencyResults) {
        if (r.category !== category || isDismissed(r.ruleId)) continue;
        if (!r.passed) {
          if (r.severity === "error" || r.evaluationError) fail++;
          else warning++;
        }
      }
      return { fail, warning };
    }

    return {
      basic: countCategory("general"),
      review: countCategory("need_review"),
      reminders: countCategory("reminders"),
    };
  }, [displayResult, isDismissed]);

  // Expert Knowledge tab badge: unfeedbacked findings count
  const expertNuanceResult = displayResult?.aiResults.find(
    (r) => r.checkId === "expert-nuance-check",
  );
  const expertNuanceFindings = expertNuanceResult?.result?.findings;
  const expertFindings: Array<{ aspectId?: string }> = Array.isArray(
    expertNuanceFindings,
  )
    ? (expertNuanceFindings as Array<{ aspectId?: string }>)
    : [];
  const expertAspectIds = expertFindings
    .map((f) => f.aspectId)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  const expertFeedbackSubmitted = useExpertFeedbackStore((s) => s.submitted);
  const projectIdStr = displayResult ? String(displayResult.projectId) : "";
  const expertUnfeedbacked = expertAspectIds.filter(
    (id) => !expertFeedbackSubmitted.has(`${projectIdStr}::${id}`),
  ).length;

  const comparisonTabCounts = useMemo(() => {
    if (!comparisonResult) return { fail: 0, warning: 0 };
    return {
      fail: comparisonResult.summary.conflicts,
      warning: Math.max(
        0,
        comparisonResult.summary.findings - comparisonResult.summary.conflicts,
      ),
    };
  }, [comparisonResult]);

  // Calculate duration
  const duration = useMemo(() => {
    if (!projectInfo?.startDate || !projectInfo?.endDate) return null;
    const start = new Date(projectInfo.startDate);
    const end = new Date(projectInfo.endDate);
    const days =
      Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} days`;
  }, [projectInfo]);

  return (
    <div className="max-w-[1400px] mx-auto p-6 md:p-8 pb-20">
      {variant === "entry" && (
        <>
          {/* Breadcrumb */}
          <nav className="flex items-center text-xs text-gray-500 mb-4 gap-2">
            <a
              href="/"
              className="hover:text-gray-800 transition-colors"
              title="トップページへ"
            >
              Request Management
            </a>
            {projectInfo && (
              <>
                <ChevronRight size={12} />
                <span>
                  【{projectInfo.tourCode ?? projectInfo.id}】
                  {projectInfo.displayName}
                </span>
              </>
            )}
          </nav>

          {/* Page Title */}
          {projectInfo && (
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 tracking-tight">
              【{projectInfo.tourCode ?? projectInfo.id}】
              {projectInfo.displayName}
            </h1>
          )}
        </>
      )}

      {/* Language selector */}
      {(() => {
        const langSwitchDisabled =
          isRunning ||
          runProgress.status === "disconnected" ||
          runProgress.status === "aborted";
        return (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-gray-500 font-medium">
              Output language:
            </span>
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => void handleLanguageSwitch("en")}
                disabled={langSwitchDisabled}
                className={`px-3 py-1 text-xs font-medium transition-colors disabled:opacity-40 ${
                  selectedLanguage === "en"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => void handleLanguageSwitch("ja")}
                disabled={langSwitchDisabled}
                className={`px-3 py-1 text-xs font-medium transition-colors border-l border-gray-200 disabled:opacity-40 ${
                  selectedLanguage === "ja"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                JA
              </button>
            </div>
            {runLanguage !== selectedLanguage &&
              !langSwitchDisabled &&
              allowRunActions && (
                <span className="flex items-center gap-2 text-xs text-amber-600">
                  No {selectedLanguage.toUpperCase()} result cached.
                  <button
                    type="button"
                    onClick={() => {
                      const pid = Number(projectId);
                      if (pid > 0) {
                        setProgressDismissed(false);
                        setRunLanguage(selectedLanguage);
                        if (activeProjectDataInput) {
                          runCheck(pid, selectedLanguage, {
                            mode: "single-project-data",
                            conversionId: activeProjectDataInput.conversionId,
                            projectData: activeProjectDataInput.projectData,
                            projectDataSource: activeProjectDataInput.source,
                            projectDataComparison:
                              activeProjectDataInput.comparison,
                            mergeCheckCache:
                              activeProjectDataInput.mergeCheckCache,
                          });
                        } else {
                          runCheck(pid, selectedLanguage);
                        }
                      }
                    }}
                    className="underline font-medium hover:text-amber-700"
                  >
                    Run now
                  </button>
                </span>
              )}
          </div>
        );
      })()}

      {/* Embedded (project) variant: simple guidance instead of the entry
          empty-state / source workspace. */}
      {variant === "project" &&
        !displayResult &&
        !comparisonResult &&
        !freeformProjectData &&
        !isRunning &&
        runProgress.status !== "error" && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            <p>
              まだこの案件の
              {selectedLanguage === "ja" ? "日本語" : "英語"}
              のAIチェック結果がありません。
            </p>
            {/* **ここは「チェックを実行」だけを出す。** 以前は「旅程を取り込む…」
                を出していたが、この空表示は言語を切り替えたときにも出る。
                EN のチェックを終えて JA に切り替えただけの人に旅程の取り込みを
                促すのは筋が違う（データは既にあり、要るのはその言語での実行）。 */}
            {allowRunActions && (
              <button
                type="button"
                onClick={() => {
                  const pid = Number(projectId) || initialProjectId || 0;
                  if (pid <= 0) return;
                  setProgressDismissed(false);
                  setRunLanguage(selectedLanguage);
                  if (activeProjectDataInput) {
                    runCheck(pid, selectedLanguage, {
                      mode: "single-project-data",
                      conversionId: activeProjectDataInput.conversionId,
                      projectData: activeProjectDataInput.projectData,
                      projectDataSource: activeProjectDataInput.source,
                      projectDataComparison: activeProjectDataInput.comparison,
                      mergeCheckCache: activeProjectDataInput.mergeCheckCache,
                    });
                  } else {
                    runCheck(pid, selectedLanguage);
                  }
                }}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                チェックを実行
              </button>
            )}
          </div>
        )}

      {/* 旅程を取り込んだ（未チェック）状態の導線。旅程確認・チェック実行・Freeform を
          前面に出す（＝旅程取り込みで CRM を Load した時と同等の操作性）。 */}
      {variant === "project" &&
        activeProjectDataInput &&
        !displayResult &&
        !comparisonResult &&
        !isRunning && (
          <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3">
            <p className="text-sm text-gray-700">
              旅程データを取り込みました（未チェック）。内容を確認してチェックを実行できます。
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/* 主アクション: この旅程に対して「何をするか」 */}
              <button
                type="button"
                onClick={() => {
                  if (activeProjectDataInput) {
                    handleRunProjectData(activeProjectDataInput);
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                チェックを実行
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("freeform")}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Freeformを開く
              </button>
              {/* ユーティリティ: 別ウィンドウで内容を表示するだけ（実行はしない）。
                  右寄せ・淡色・別窓アイコンで主アクションと視覚的に区別する。 */}
              <button
                type="button"
                onClick={() => handleOpenProjectDataOverview(null, "freeform")}
                title="別ウィンドウで旅程データを表示（チェックは実行しません）"
                className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-gray-500 hover:bg-white hover:text-indigo-600"
              >
                <Database size={13} />
                旅程を表示
                <ExternalLink size={12} className="opacity-70" />
              </button>
            </div>
          </div>
        )}

      {variant === "entry" && (
        <SourceWorkspace
          projectId={projectId}
          selectedLanguage={selectedLanguage}
          activeSourceLabel={activeProjectDataInput?.sourceLabel ?? null}
          restoredProjectDataInput={activeProjectDataInput}
          restoreProjectDataInputKey={sourceWorkspaceRestoreKey}
          isRunning={isRunning}
          onRunProjectData={handleRunProjectData}
          onFreeformReviewProjectData={handleFreeformReviewProjectData}
          onLoadCachedProjectData={handleLoadCachedProjectData}
          onLoadCachedMergeCheck={handleLoadCachedMergeCheck}
          onComparisonResult={handleComparisonResult}
        />
      )}

      {/* Share Bundle は常設バーから撤去し、管理者向けキャッシュ管理モーダルへ移設
          （→ CacheManagementModal）。この input はモーダルの Import ボタンから
          shareBundleInputRef 経由でトリガーされるため、非表示のまま残す。 */}
      <input
        ref={shareBundleInputRef}
        type="file"
        accept=".json.zip,.zip,application/zip,application/x-zip-compressed,application/json,.json"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void handleImportShareBundle(file);
        }}
      />

      {/* CRM取り込みダイアログ: AIチェック無しでも取り込めるように
          (a) 言語を選んでチェック実行 / (b) 旅程のみ取り込み(チェックしない) */}
      {ingestDialogOpen && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            if (!ingestBusy) setIngestDialogOpen(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-800">
              CRMデータの取り込み
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              この案件にはまだAIチェック結果がありません。どうしますか？
            </p>

            <div className="mt-4">
              <span className="text-xs font-medium text-gray-600">
                出力言語
              </span>
              <div className="mt-1 inline-flex overflow-hidden rounded-md border border-gray-300">
                {(["ja", "en"] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setSelectedLanguageState(lang)}
                    className={`px-3 py-1.5 text-xs font-semibold ${
                      selectedLanguage === lang
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {lang === "ja" ? "日本語" : "English"}
                  </button>
                ))}
              </div>
            </div>

            {ingestError && (
              <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {ingestError}
              </p>
            )}

            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                disabled={ingestBusy || isRunning}
                onClick={handleIngestRunCheck}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                AIチェックを実行
              </button>
              <button
                type="button"
                disabled={ingestBusy}
                onClick={() => void handleIngestItineraryOnly()}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {ingestBusy
                  ? "取り込み中…"
                  : "旅程のみ取り込み（チェックしない）"}
              </button>
              <button
                type="button"
                disabled={ingestBusy}
                onClick={() => setIngestDialogOpen(false)}
                className="mt-1 text-xs text-gray-400 hover:text-gray-600"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress / Error / Cancelled — unified panel */}
      {/* Terminal states (completed/aborted/cancelled/error/disconnected) are only shown
          when runLanguage matches selectedLanguage; otherwise the panel would
          reflect a previous run in a different language. */}
      {!progressDismissed &&
        (isRunning ||
          (runLanguage === selectedLanguage &&
            (runProgress.status === "aborted" ||
              runProgress.status === "completed" ||
              runProgress.status === "cancelled" ||
              runProgress.status === "error" ||
              runProgress.status === "disconnected"))) && (
          <div className="mb-6">
            <RunProgress
              progress={runProgress}
              projectId={Number(projectId) || undefined}
              language={runLanguage}
              onAbort={() => {
                const pid = Number(projectId);
                if (pid > 0) abortRun(pid, runLanguage);
              }}
              onCancel={() => {
                const pid = Number(projectId);
                if (pid > 0) cancelRun(pid, runLanguage);
              }}
              onRetry={allowRunActions ? handleRefresh : undefined}
              onRetryFailed={allowRunActions ? handleRetryFailed : undefined}
              onAdminAction={
                allowRunActions && adminStatus === "admin"
                  ? handleAdminNodeAction
                  : undefined
              }
              // **再開は外部案件でも出す。** `allowRunActions` は「CRM から
              // 最新データを取り直す実行」を抑えるための旗で、再開はそれに
              // 当たらない（チェックポイントに保存済みの旅程データで続きを
              // 走らせるだけ）。ここまで一緒に塞いでいたため、外部案件は
              // 中断したら二度と再開できず、中間状態だけが残っていた。
              onResume={
                Number(projectId)
                  ? async () => {
                      const pid = Number(projectId);
                      if (pid <= 0) return;
                      // 繋ぎ直しは切断からの復帰と同じ経路を通す
                      // （片方だけ signal を渡し忘れる、が起きないように）。
                      if (await attachToLiveRun(pid, runLanguage)) return;
                      const resumed = await resumeFromCheckpoint(
                        pid,
                        runLanguage,
                      );
                      // **フォールバックも塞がない。** チェックポイントが
                      // 使えなかったときに通常実行へ落ちられないと、
                      // "Resuming incomplete checks..." のまま行き止まりに
                      // なる（外部案件は allowRunActions=false なので必ず
                      // そうなっていた）。利用者が Resume を押した以上、
                      // 実行を続ける意思は明確。
                      if (!resumed.resumed && resumed.fallbackToFullRun) {
                        runCheck(pid, runLanguage);
                      }
                    }
                  : undefined
              }
              onDismiss={() => setProgressDismissed(true)}
            />
          </div>
        )}

      {/* Content - show when we have check results or comparison results */}
      {(displayResult || comparisonResult || freeformProjectData) && (
        <>
          {/* Tour Header Strip */}
          {displayResult && projectInfo && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm flex flex-wrap gap-6 items-center justify-between">
              <div className="flex gap-8 items-center">
                <div>
                  <div className="text-xs text-gray-500 font-medium mb-1">
                    Trip Dates
                  </div>
                  <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    {projectInfo.startDate ?? "?"} —{" "}
                    {projectInfo.endDate ?? "?"}
                    {duration && (
                      <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
                        {duration}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-medium mb-1">
                    Travelers
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {projectInfo.travelersCount ?? "—"} PAX
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-xs text-gray-500 font-medium mb-1">
                    Last Checked
                  </div>
                  {displayResult.completedAt ? (
                    <div className="text-sm font-bold text-gray-900">
                      {new Date(displayResult.completedAt).toLocaleString()}
                    </div>
                  ) : (
                    <div className="text-sm font-bold text-blue-600 animate-pulse">
                      Running...
                    </div>
                  )}
                </div>
                <a
                  href={projectDetailPath(displayResult.projectId)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-50"
                  title="この案件のワークスペース（実行履歴・提案アクション・実績メモ・類似事例）を開く"
                >
                  <FolderKanban size={14} />
                  案件ワークスペース
                </a>
                <button
                  type="button"
                  onClick={() => handleOpenProjectDataOverview()}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700"
                  title="View project data overview"
                >
                  <Database size={14} />
                  View data
                </button>
                {!isRunning && currentResult && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowUsageInfo(!showUsageInfo)}
                      className="h-7 w-7 flex items-center justify-center rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Token usage & cost details"
                    >
                      <Info size={14} />
                    </button>
                    {showUsageInfo && (
                      <UsageInfoPopover
                        result={currentResult}
                        rateLimitInfo={rateLimitInfo}
                        onClose={() => setShowUsageInfo(false)}
                        onOpenCacheManagement={() => {
                          setShowUsageInfo(false);
                          setShowCacheManagement(true);
                        }}
                      />
                    )}
                  </div>
                )}
                {allowRunActions && (
                  <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={isRunning}
                    className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-blue-600 disabled:opacity-40 transition-colors"
                    title={
                      activeProjectDataInput
                        ? "チェックを再実行します。読み込み済みの旅程データ（外部ソース）に対して全チェックをやり直します（数分かかります）"
                        : "チェックを再実行します。CRM から最新の旅程データを取得し直したうえで全チェックをやり直します（数分かかります）"
                    }
                  >
                    <RefreshCcw size={15} />
                  </button>
                )}
              </div>
            </div>
          )}

          {showManagementBar && displayResult && (
            <ProjectManagementBar
              projectParam={String(displayResult.projectId)}
              refreshKey={displayResult.completedAt ?? undefined}
            />
          )}

          {/* Stale results banner */}
          {isStale && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 flex items-center gap-2">
              <RefreshCcw size={14} className="text-blue-500 animate-spin" />
              <span className="text-sm text-blue-700 font-medium">
                Refreshing results... Previous results shown below.
              </span>
            </div>
          )}

          {/* Results content — dimmed when showing stale results */}
          <div
            className={
              isStale
                ? "opacity-60 transition-opacity duration-300"
                : "transition-opacity duration-300"
            }
          >
            {/* Results Summary */}
            {displayResult && (
              <ResultsSummary
                result={displayResult}
                isDismissed={isDismissed}
              />
            )}

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <div
                role="tablist"
                className="flex items-center gap-1 overflow-x-auto no-scrollbar"
              >
                {displayResult && (
                  <>
                    <button
                      id="results-tab-basic"
                      role="tab"
                      type="button"
                      onClick={() => handleManualTabChange("basic")}
                      aria-selected={activeTab === "basic"}
                      aria-controls="results-panel-basic"
                      tabIndex={activeTab === "basic" ? 0 : -1}
                      className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                        activeTab === "basic"
                          ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                          : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        Basic Requests
                        <TabIssueBadges counts={tabCounts.basic} />
                      </span>
                    </button>
                    <button
                      id="results-tab-review"
                      role="tab"
                      type="button"
                      onClick={() => handleManualTabChange("review")}
                      aria-selected={activeTab === "review"}
                      aria-controls="results-panel-review"
                      tabIndex={activeTab === "review" ? 0 : -1}
                      className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                        activeTab === "review"
                          ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                          : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        Need Review
                        <TabIssueBadges counts={tabCounts.review} />
                      </span>
                    </button>
                    <button
                      id="results-tab-reminders"
                      role="tab"
                      type="button"
                      onClick={() => handleManualTabChange("reminders")}
                      aria-selected={activeTab === "reminders"}
                      aria-controls="results-panel-reminders"
                      tabIndex={activeTab === "reminders" ? 0 : -1}
                      className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                        activeTab === "reminders"
                          ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                          : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        Reminders
                        <TabIssueBadges counts={tabCounts.reminders} />
                      </span>
                    </button>
                    {expertAspectIds.length > 0 && (
                      <button
                        id="results-tab-expert-knowledge"
                        role="tab"
                        type="button"
                        onClick={() =>
                          handleManualTabChange("expert-knowledge")
                        }
                        aria-selected={activeTab === "expert-knowledge"}
                        aria-controls="results-panel-expert-knowledge"
                        tabIndex={activeTab === "expert-knowledge" ? 0 : -1}
                        className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                          activeTab === "expert-knowledge"
                            ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                            : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          エキスパート知識
                          {expertUnfeedbacked > 0 && (
                            <span className="bg-red-100 text-red-600 h-4 px-1 text-[10px] inline-flex items-center rounded-sm">
                              {expertUnfeedbacked}
                            </span>
                          )}
                        </span>
                      </button>
                    )}
                    <button
                      id="results-tab-findings"
                      role="tab"
                      type="button"
                      onClick={() => handleManualTabChange("findings")}
                      aria-selected={activeTab === "findings"}
                      aria-controls="results-panel-findings"
                      tabIndex={activeTab === "findings" ? 0 : -1}
                      className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                        activeTab === "findings"
                          ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                          : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                      }`}
                      title="全チェック横断の指摘を重症度順に一覧し、その場で対応判断ができます"
                    >
                      指摘一覧
                    </button>
                    {/* 案件ページ埋め込み時はページ側に実行履歴パネルがあるため重複させない */}
                    {variant !== "project" && (
                      <button
                        id="results-tab-history"
                        role="tab"
                        type="button"
                        onClick={() => handleManualTabChange("history")}
                        aria-selected={activeTab === "history"}
                        aria-controls="results-panel-history"
                        tabIndex={activeTab === "history" ? 0 : -1}
                        className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                          activeTab === "history"
                            ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                            : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                        }`}
                        title="この案件の実行履歴と、前回からの新規/継続/解消の差分"
                      >
                        差分・履歴
                      </button>
                    )}
                  </>
                )}
                {freeformProjectData && (
                  <button
                    id="results-tab-freeform"
                    role="tab"
                    type="button"
                    onClick={() => handleManualTabChange("freeform")}
                    aria-selected={activeTab === "freeform"}
                    aria-controls="results-panel-freeform"
                    tabIndex={activeTab === "freeform" ? 0 : -1}
                    className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                      activeTab === "freeform"
                        ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                        : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                    }`}
                  >
                    Freeform Review
                  </button>
                )}
                {comparisonResult && (
                  <button
                    id="results-tab-comparison"
                    role="tab"
                    type="button"
                    onClick={() => handleManualTabChange("comparison")}
                    aria-selected={activeTab === "comparison"}
                    aria-controls="results-panel-comparison"
                    tabIndex={activeTab === "comparison" ? 0 : -1}
                    className={`px-4 py-3 text-sm transition-all whitespace-nowrap ${
                      activeTab === "comparison"
                        ? "font-bold text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                        : "font-medium text-gray-500 hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      Comparison
                      <TabIssueBadges counts={comparisonTabCounts} />
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Main Content Card */}
            <div className="border border-gray-200 shadow-sm bg-white overflow-hidden min-h-[600px] rounded-lg">
              {/* Card Header */}
              <div className="border-b border-gray-100 bg-white px-6 py-4 sticky top-0 z-10">
                <h2 className="text-lg font-bold text-gray-800">
                  <span hidden={activeTab !== "basic"}>Basic Requests</span>
                  <span hidden={activeTab !== "review"}>Need Review</span>
                  <span hidden={activeTab !== "reminders"}>Reminders</span>
                  <span hidden={activeTab !== "expert-knowledge"}>
                    エキスパート知識
                  </span>
                  <span hidden={activeTab !== "comparison"}>Comparison</span>
                  <span hidden={activeTab !== "history"}>差分・履歴</span>
                  <span hidden={activeTab !== "findings"}>指摘一覧</span>
                  <span hidden={activeTab !== "freeform"}>Freeform Review</span>
                </h2>
              </div>

              {/* Card Content */}
              <div className="p-6">
                {displayResult && (
                  <>
                    <BasicRequestsTab
                      result={displayResult}
                      activeTab={activeTab}
                      showExecutiveSummary={!isRunning && !!currentResult}
                      onJumpToTab={(tab) => setActiveTab(tab)}
                      onTraceabilityHistoryPush={handleTraceabilityHistoryPush}
                      onReturnToSummary={handleReturnToSummary}
                      onProjectDataReferenceClick={
                        handleOpenProjectDataOverview
                      }
                      onJumpToHistory={
                        // 案件ページ埋め込み(project)では履歴タブが無いためクリック遷移しない
                        variant !== "project"
                          ? () => handleManualTabChange("history")
                          : undefined
                      }
                    />
                    <NeedReviewTab
                      result={displayResult}
                      activeTab={activeTab}
                      onReturnToSummary={handleReturnToSummary}
                      onProjectDataReferenceClick={
                        handleOpenProjectDataOverview
                      }
                    />
                    <RemindersTab
                      result={displayResult}
                      activeTab={activeTab}
                      onReturnToSummary={handleReturnToSummary}
                      onProjectDataReferenceClick={
                        handleOpenProjectDataOverview
                      }
                    />
                    {expertAspectIds.length > 0 && (
                      <ExpertKnowledgeTab
                        result={displayResult}
                        activeTab={activeTab}
                      />
                    )}
                    <FindingsTab result={displayResult} activeTab={activeTab} />
                    {variant !== "project" && (
                      <HistoryTab
                        result={displayResult}
                        activeTab={activeTab}
                      />
                    )}
                  </>
                )}
                {freeformProjectData && (
                  <FreeformTab
                    activeTab={activeTab}
                    projectId={Number(projectId) || undefined}
                    projectData={freeformProjectData}
                    language={selectedLanguage}
                    source={freeformProjectDataInput?.source}
                    initialTemplateId={freeformRestoreTarget?.templateId}
                    initialPrompt={freeformRestoreTarget?.prompt}
                    remountKey={
                      freeformRestoreTarget?.cacheKey ??
                      `${projectId}:${selectedLanguage}:freeform`
                    }
                    onProjectDataReferenceClick={(source) =>
                      handleOpenProjectDataOverview(source, "freeform")
                    }
                    onResultChange={setFreeformReviewResult}
                  />
                )}
                {comparisonResult && (
                  <ComparisonTab
                    result={comparisonResult}
                    activeTab={activeTab}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty state (entry variant only) */}
      {variant === "entry" &&
        !displayResult &&
        !comparisonResult &&
        !freeformProjectData &&
        !isRunning &&
        runProgress.status !== "error" && (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-400">
            <p className="text-lg font-medium mb-2 text-gray-600">
              案件を開いてチェックを始めましょう
            </p>
            <div className="mb-5 grid max-w-3xl grid-cols-1 gap-2 text-left text-xs text-gray-500 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="font-semibold text-gray-700">① CRM 案件を検索</p>
                <p className="mt-1">
                  右上の検索ボックスに旅程ID・ツアー番号・名前を入力
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="font-semibold text-gray-700">② 外部旅程を登録</p>
                <p className="mt-1">
                  上の入力エリアで Excel / URL を変換 →「案件として登録」
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="font-semibold text-gray-700">③ 続きから再開</p>
                <p className="mt-1">下の最近の案件から開く（履歴・差分つき）</p>
              </div>
            </div>
            {serverProjects.length > 0 && (
              <div className="mb-6 w-full max-w-3xl">
                <p className="text-xs text-gray-400 mb-2 text-center">
                  保存済みの案件（サーバ履歴）
                </p>
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  {serverProjects.slice(0, 10).map((project) => (
                    <button
                      key={`server-project:${project.projectKey}`}
                      type="button"
                      onClick={() => {
                        // ia-ui Step3: / は入口専用。CRM 案件も案件ページへ遷移
                        // する（外部案件は保存済みスナップショットを持つ案件ページへ）。
                        window.location.href = projectDetailPath(
                          project.projectKey,
                          project.sourceKind !== "external" &&
                            project.lastRunLanguage
                            ? { lang: project.lastRunLanguage }
                            : undefined,
                        );
                      }}
                      className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-left text-sm transition-colors last:border-b-0 hover:bg-blue-50"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium text-gray-700">
                        {project.externalRef ?? `#${project.displayId}`} ·{" "}
                        {project.title || `Project ${project.displayId}`}
                      </span>
                      {project.sourceKind === "external" && (
                        <span
                          className="max-w-[10rem] shrink truncate rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700"
                          title={project.sourceLabel ?? "外部データ"}
                        >
                          {project.sourceLabel ?? "外部データ"}
                        </span>
                      )}
                      {project.env && project.env !== "default" && (
                        <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium uppercase text-gray-500">
                          {project.env}
                        </span>
                      )}
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                        {PROJECT_STAGE_LABELS[project.stage] ?? project.stage}
                      </span>
                      {project.lastRunLanguage && (
                        <span className="shrink-0 text-[11px] uppercase text-gray-400">
                          {project.lastRunLanguage}
                        </span>
                      )}
                      {project.lastRunAt && (
                        <span className="shrink-0 text-[11px] text-gray-400">
                          {new Date(project.lastRunAt).toLocaleDateString(
                            "ja-JP",
                          )}
                        </span>
                      )}
                      {project.lastOverallScore != null && (
                        <span className="shrink-0 text-[11px] font-semibold text-gray-500">
                          {project.lastOverallScore}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-center text-[11px] text-gray-400">
                  <a href="/projects" className="text-blue-500 hover:underline">
                    案件一覧
                  </a>
                  で全件・実行履歴・差分を確認できます
                </p>
              </div>
            )}
            {(() => {
              const recentByProject = new Map<string, RecentProjectItem>();
              const recentProjectIdBySource = new Map<string, number>();
              for (const entry of getHistory()) {
                const cached = entry.language
                  ? getResult(entry.projectId, entry.language).result
                  : null;
                const sourceKey = recentProjectSourceKey(
                  cached?.projectDataSource,
                  entry.language,
                  entry.projectId,
                );
                if (sourceKey) {
                  recentProjectIdBySource.set(sourceKey, entry.projectId);
                }
                mergeRecentProjectItem(recentByProject, {
                  sortAt: entry.runAt,
                  projectId: entry.projectId,
                  language: entry.language,
                  projectTitle:
                    entry.projectTitle ||
                    (cached ? projectTitleFromRunResult(cached) : null) ||
                    `Project ${entry.projectId}`,
                  sourceLabel:
                    entry.sourceLabel ?? cached?.projectDataSource?.label,
                  usesExternalProjectData:
                    entry.usesExternalProjectData ??
                    isExternalProjectDataSource(cached?.projectDataSource),
                  hasCheckResult: true,
                });
              }
              for (const entry of listFreeformReviewResultCaches()) {
                const sourceKey = recentProjectSourceKey(
                  entry.source,
                  entry.language,
                  entry.projectId,
                );
                const recentProjectId = entry.source
                  ? ((sourceKey
                      ? recentProjectIdBySource.get(sourceKey)
                      : null) ??
                    projectIdFromProjectDataSource(
                      entry.source,
                      String(entry.projectId),
                    ))
                  : entry.projectId;
                mergeRecentProjectItem(recentByProject, {
                  sortAt: entry.cachedAt,
                  projectId: recentProjectId,
                  projectTitle:
                    entry.source?.label ||
                    entry.stage ||
                    entry.templateName ||
                    `Project ${entry.projectId}`,
                  language: entry.language,
                  sourceLabel: entry.source?.label,
                  usesExternalProjectData: isExternalProjectDataSource(
                    entry.source,
                  ),
                  freeformCache: entry,
                  hasCheckResult: false,
                });
              }
              const recentProjects = [...recentByProject.values()]
                .sort((a, b) => b.sortAt.localeCompare(a.sortAt))
                .slice(0, 8);
              if (recentProjects.length === 0) return null;
              return (
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-2">Recent projects</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {recentProjects.map((h) => (
                      <button
                        key={`recent:${recentProjectItemKey(h)}`}
                        type="button"
                        onClick={() => {
                          if (!h.hasCheckResult && h.freeformCache) {
                            void handleLoadFreeformCachedProject(
                              h.freeformCache,
                            );
                            return;
                          }
                          // ia-ui Step3: CRM 案件は案件ページで実行・閲覧する。
                          // 外部データのキャッシュ復元のみ入口に残す（未登録の
                          // 変換結果はローカルキャッシュにしか存在しないため）。
                          if (!h.usesExternalProjectData) {
                            window.location.href = projectDetailPath(
                              h.projectId,
                              h.language ? { lang: h.language } : undefined,
                            );
                            return;
                          }
                          void handleLoadProject(h.projectId, h.language);
                        }}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-blue-100 hover:text-blue-700"
                      >
                        <span className="min-w-0 truncate">
                          #{h.projectId} · {h.projectTitle}
                        </span>
                        {h.language && (
                          <span className="shrink-0 text-[11px] uppercase text-gray-400">
                            {h.language}
                          </span>
                        )}
                        {h.usesExternalProjectData && (
                          <span
                            className="max-w-[12rem] shrink truncate rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700"
                            title={h.sourceLabel ?? "External data"}
                          >
                            {h.sourceLabel ?? "External"}
                          </span>
                        )}
                        {h.freeformCache && (
                          <span
                            className="max-w-[12rem] shrink truncate rounded-full bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700"
                            title={h.freeformCache.stage}
                          >
                            Freeform
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      <CacheManagementModal
        open={showCacheManagement}
        onClose={() => setShowCacheManagement(false)}
        currentProjectId={Number(projectId) || null}
        shareBundle={
          adminStatus === "admin"
            ? {
                onDownload: handleDownloadShareBundle,
                downloadDisabled: !shareBundleResult,
                onImportClick: () => shareBundleInputRef.current?.click(),
                status: shareBundleStatus,
              }
            : null
        }
      />
      <ProjectDataOverviewDialog
        open={showProjectDataOverview}
        popupWindow={projectDataOverviewWindow}
        focusKey={projectDataOverviewFocusKey}
        focusSource={projectDataOverviewFocusSource}
        projectData={overviewProjectData}
        checkResult={overviewCheckResult}
        freeformResult={freeformReviewResult}
        mode={projectDataOverviewMode}
        sourceLabel={overviewSourceLabel}
        onModeChange={setProjectDataOverviewMode}
        onClose={handleCloseProjectDataOverview}
        onCheckReferenceClick={handleOpenCheckFromProjectDataOverview}
      />
      <ShareBundleImportDialog
        pending={pendingShareBundleImport}
        isImporting={isShareBundleImporting}
        onCancel={cancelImportShareBundle}
        onConfirm={confirmImportShareBundle}
      />
    </div>
  );
}

function ShareBundleImportDialog({
  pending,
  isImporting,
  onCancel,
  onConfirm,
}: {
  pending: PendingShareBundleImport | null;
  isImporting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!pending) return null;

  const { preview } = pending;
  const hasOverwrites = preview.overwriteCount > 0;
  const visibleOverwrites = preview.overwrites.slice(0, 8);
  const remainingOverwrites = preview.overwriteCount - visibleOverwrites.length;

  return (
    <DialogShell
      onClose={onCancel}
      escapeToClose={!isImporting}
      backdropDisabled={isImporting}
      ariaLabelledBy="share-bundle-import-title"
      backdropAriaLabel="Cancel share bundle import"
      panelClassName="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-gray-900">
            <Upload size={18} className="text-blue-600" />
            <h2
              id="share-bundle-import-title"
              className="text-base font-semibold"
            >
              Import Share Bundle
            </h2>
          </div>
          <div className="mt-1 truncate text-xs text-gray-500">
            {preview.fileName}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={isImporting}
          className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Cancel share bundle import"
        >
          <X size={18} />
        </button>
      </div>

      <div className="min-h-0 overflow-y-auto px-5 py-4">
        <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
          <div className="text-[11px] font-semibold uppercase text-blue-500">
            Itinerary project
          </div>
          <div className="mt-1 text-sm font-semibold text-blue-950">
            {preview.projectName}
          </div>
        </div>

        <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
          <div>
            <span className="font-semibold text-gray-500">Project ID:</span>{" "}
            {preview.projectId}
          </div>
          <div>
            <span className="font-semibold text-gray-500">Language:</span>{" "}
            {preview.language.toUpperCase()}
          </div>
          <div>
            <span className="font-semibold text-gray-500">Completed:</span>{" "}
            {new Date(preview.completedAt).toLocaleString()}
          </div>
          <div>
            <span className="font-semibold text-gray-500">Exported:</span>{" "}
            {new Date(preview.exportedAt).toLocaleString()}
          </div>
          {preview.sourceLabel && (
            <div className="min-w-0 sm:col-span-2">
              <span className="font-semibold text-gray-500">Source:</span>{" "}
              <span className="break-words">{preview.sourceLabel}</span>
            </div>
          )}
          <div>
            <span className="font-semibold text-gray-500">Cache items:</span>{" "}
            {preview.storageEntryCount}
          </div>
          <div>
            <span className="font-semibold text-gray-500">Detail items:</span>{" "}
            {preview.sourceDetailCount}
          </div>
          <div>
            <span className="font-semibold text-gray-500">
              Freeform results:
            </span>{" "}
            {preview.freeformReviewResultCount}
          </div>
        </div>

        {hasOverwrites ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
            <div className="flex items-start gap-2 text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold">
                  Existing cache will be overwritten.
                </div>
                <div className="mt-1 text-xs leading-relaxed text-amber-800">
                  Confirm only if you want this browser cache replaced by the
                  imported share bundle.
                </div>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5">
              {visibleOverwrites.map((item) => (
                <li
                  key={`${item.storage}:${item.key}`}
                  className="rounded border border-amber-200 bg-white/70 px-2 py-1 text-xs text-amber-900"
                >
                  <span className="font-semibold">{item.label}</span>{" "}
                  <span className="text-amber-700">({item.storage})</span>
                </li>
              ))}
            </ul>
            {remainingOverwrites > 0 && (
              <div className="mt-2 text-xs font-medium text-amber-800">
                +{remainingOverwrites} more cache item
                {remainingOverwrites > 1 ? "s" : ""}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
            No existing cache will be overwritten.
          </div>
        )}
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-5 py-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isImporting}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isImporting}
          className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            hasOverwrites
              ? "bg-amber-600 hover:bg-amber-700"
              : "bg-gray-900 hover:bg-gray-800"
          }`}
        >
          {isImporting
            ? "Importing..."
            : hasOverwrites
              ? "Overwrite and Import"
              : "Import"}
        </button>
      </div>
    </DialogShell>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

function UsageInfoPopover({
  result,
  rateLimitInfo,
  onClose,
  onOpenCacheManagement,
}: {
  result: AICheckRunResult;
  rateLimitInfo: RateLimitInfo | null;
  onClose: () => void;
  onOpenCacheManagement: () => void;
}) {
  const [showPerModel, setShowPerModel] = useState(false);
  const [showPerCheck, setShowPerCheck] = useState(false);
  const [showRateLimit, setShowRateLimit] = useState(false);

  const modelUsage = result.modelUsage ?? [];
  const totalDuration = modelUsage.reduce((sum, m) => sum + m.durationMs, 0);
  const totalCost = modelUsage.reduce((sum, m) => sum + m.estimatedCost, 0);

  // Build per-model cost-per-token rates from modelUsage for per-check cost estimation
  const modelRates = useMemo(() => {
    const rates = new Map<string, { inputRate: number; outputRate: number }>();
    for (const m of modelUsage) {
      if (m.tokensUsed.total > 0) {
        // Derive effective rates: cost = (prompt/1M)*inputRate + (completion/1M)*outputRate
        // Use proportional split based on actual token counts
        const totalInputCost =
          m.tokensUsed.prompt > 0
            ? m.estimatedCost *
              (m.tokensUsed.prompt /
                (m.tokensUsed.prompt + m.tokensUsed.completion))
            : 0;
        const totalOutputCost = m.estimatedCost - totalInputCost;
        rates.set(m.model, {
          inputRate:
            m.tokensUsed.prompt > 0
              ? (totalInputCost / m.tokensUsed.prompt) * 1_000_000
              : 0,
          outputRate:
            m.tokensUsed.completion > 0
              ? (totalOutputCost / m.tokensUsed.completion) * 1_000_000
              : 0,
        });
      }
    }
    return rates;
  }, [modelUsage]);

  const estimateCheckCost = useCallback(
    (
      model: string | undefined,
      tokens: { prompt: number; completion: number },
    ) => {
      if (!model || (tokens.prompt === 0 && tokens.completion === 0)) return 0;
      // Try exact match first, then prefix
      const rates =
        modelRates.get(model) ??
        Array.from(modelRates.entries()).find(([k]) =>
          model.startsWith(k),
        )?.[1];
      if (!rates) return 0;
      return (
        (tokens.prompt / 1_000_000) * rates.inputRate +
        (tokens.completion / 1_000_000) * rates.outputRate
      );
    },
    [modelRates],
  );

  // Per-check breakdown: profile + ai checks (batch checks aggregated), sorted by cost desc
  type PerCheckItem = {
    name: string;
    model: string;
    fallbackFrom?: string;
    tokens: { prompt: number; completion: number; total: number };
    durationMs: number;
    cost: number;
    checkCount: number;
    checkNames?: string[];
    status: "pass" | "warning" | "fail" | "error" | "skipped" | "ok";
    errors?: string[];
  };

  const perCheckItems = useMemo(() => {
    const items: PerCheckItem[] = [];

    // Contact Topics Compaction
    const ct = result.contactTopicsResult;
    if (ct && (ct.tokensUsed.total > 0 || ct.error)) {
      items.push({
        name: "Contact Compaction",
        model: ct.model ?? "—",
        fallbackFrom: ct.fallbackFrom,
        tokens: ct.tokensUsed,
        durationMs: ct.durationMs ?? 0,
        cost: estimateCheckCost(ct.model, ct.tokensUsed),
        checkCount: 1,
        status: ct.error ? "error" : "ok",
        errors: ct.error ? [ct.error] : undefined,
      });
    }

    // Customer profile
    const cp = result.customerProfileResult;
    if (cp && (cp.tokensUsed.total > 0 || cp.error)) {
      items.push({
        name: "Customer Profile",
        model: cp.model ?? "—",
        fallbackFrom: cp.fallbackFrom,
        tokens: cp.tokensUsed,
        durationMs: cp.durationMs ?? 0,
        cost: estimateCheckCost(cp.model, cp.tokensUsed),
        checkCount: 1,
        status: cp.error ? "error" : "ok",
        errors: cp.error ? [cp.error] : undefined,
      });
    }

    // AI checks — group by batchGroupName
    // Group by batchGroupName + model (different models after fallback/split get separate groups)
    const batchMap = new Map<string, PerCheckItem & { checkNames: string[] }>();
    for (const r of result.aiResults) {
      if (r.batchGroupName) {
        const groupKey = `${r.batchGroupName}::${r.model ?? "default"}`;
        const existing = batchMap.get(groupKey);
        if (existing) {
          existing.tokens.prompt += r.tokensUsed.prompt;
          existing.tokens.completion += r.tokensUsed.completion;
          existing.tokens.total += r.tokensUsed.total;
          existing.durationMs = Math.max(
            existing.durationMs,
            r.durationMs ?? 0,
          );
          existing.cost += estimateCheckCost(r.model, r.tokensUsed);
          existing.checkCount += 1;
          existing.checkNames.push(r.checkName);
          if (r.fallbackFrom && !existing.fallbackFrom) {
            existing.fallbackFrom = r.fallbackFrom;
          }
          if (r.status === "error" || r.status === "skipped") {
            existing.status = "error";
            if (r.error) {
              if (!existing.errors) existing.errors = [];
              existing.errors.push(`${r.checkName}: ${r.error}`);
            }
          }
        } else {
          batchMap.set(groupKey, {
            name: r.batchGroupName,
            model: r.model ?? "—",
            fallbackFrom: r.fallbackFrom,
            tokens: { ...r.tokensUsed },
            durationMs: r.durationMs ?? 0,
            cost: estimateCheckCost(r.model, r.tokensUsed),
            checkCount: 1,
            checkNames: [r.checkName],
            status:
              r.status === "error" || r.status === "skipped" ? "error" : "ok",
            errors: r.error ? [`${r.checkName}: ${r.error}`] : undefined,
          });
        }
      } else {
        items.push({
          name: r.checkName,
          model: r.model ?? "—",
          fallbackFrom: r.fallbackFrom,
          tokens: r.tokensUsed,
          durationMs: r.durationMs ?? 0,
          cost: estimateCheckCost(r.model, r.tokensUsed),
          checkCount: 1,
          status: r.status,
          errors: r.error ? [r.error] : undefined,
        });
      }
    }

    // Reconstruct batch duration: per-check durationMs is divided equally,
    // so actual call duration = perCheck * checkCount
    for (const batch of batchMap.values()) {
      batch.durationMs = batch.durationMs * batch.checkCount;
      items.push(batch);
    }

    items.sort((a, b) => b.cost - a.cost);
    return items;
  }, [result, estimateCheckCost]);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-8 z-50 w-96 max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 p-4 text-sm">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-gray-800">Usage Summary</h4>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Total */}
        <div className="bg-gray-50 rounded-md p-3 mb-3">
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-xs text-gray-500 font-medium">
              Total Tokens
            </span>
            <span className="font-bold text-gray-800">
              {result.totalTokensUsed.total.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-baseline text-xs text-gray-500">
            <span>
              {result.totalTokensUsed.prompt.toLocaleString()} in /{" "}
              {result.totalTokensUsed.completion.toLocaleString()} out
            </span>
            <span className="font-medium text-gray-700">
              ~${totalCost.toFixed(4)}
            </span>
          </div>
          {(() => {
            const wallMs =
              new Date(result.completedAt).getTime() -
              new Date(result.startedAt).getTime();
            const hasWall = wallMs > 0;
            const hasApi = totalDuration > 0;
            if (!hasWall && !hasApi) return null;
            return (
              <>
                {hasWall && (
                  <div className="flex justify-between items-baseline text-xs text-gray-500 mt-1">
                    <span>Wall-clock time</span>
                    <span>{formatDuration(wallMs)}</span>
                  </div>
                )}
                {hasApi && (
                  <div className="flex justify-between items-baseline text-xs text-gray-500 mt-0.5">
                    <span>Cumulative API time</span>
                    <span>{formatDuration(totalDuration)}</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Fallback summary */}
        {(() => {
          const fallbackCount = perCheckItems.filter(
            (i) => i.fallbackFrom,
          ).length;
          if (fallbackCount === 0) return null;
          return (
            <div className="text-xs text-amber-600 bg-amber-50 rounded-md px-2 py-1 mb-3">
              {fallbackCount} check{fallbackCount !== 1 ? "s" : ""} fell back to
              gpt-4.1-* due to context overflow
            </div>
          );
        })()}

        {/* Per-model breakdown (collapsible) */}
        {modelUsage.length > 0 && (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => setShowPerModel(!showPerModel)}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors w-full"
            >
              {showPerModel ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              Per Model ({modelUsage.length})
            </button>
            {showPerModel && (
              <div className="mt-2 space-y-2">
                {modelUsage.map((m) => (
                  <div
                    key={m.model}
                    className="bg-blue-50/50 border border-blue-100 rounded-md p-2.5"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-gray-800 text-xs">
                        {m.model}
                      </span>
                      <span className="text-xs text-gray-500">
                        {m.callCount} call{m.callCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="text-gray-400">Tokens</div>
                        <div className="font-medium text-gray-700">
                          {m.tokensUsed.total.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400">Time</div>
                        <div className="font-medium text-gray-700">
                          {m.durationMs > 0
                            ? formatDuration(m.durationMs)
                            : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400">Cost</div>
                        <div className="font-medium text-gray-700">
                          ${m.estimatedCost.toFixed(4)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Per-check breakdown (collapsible) */}
        {perCheckItems.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowPerCheck(!showPerCheck)}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors w-full"
            >
              {showPerCheck ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              Per Check ({perCheckItems.length})
            </button>
            {showPerCheck && (
              <div className="mt-2 space-y-1.5">
                {perCheckItems.map((item) => (
                  <div
                    key={item.name}
                    className={`border rounded-md p-2 ${
                      item.status === "error" || item.status === "skipped"
                        ? "border-red-200 bg-red-50/50"
                        : "border-gray-150 bg-gray-50/50"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {item.status === "error" && (
                          <span
                            className="shrink-0 h-2 w-2 rounded-full bg-red-500"
                            title="Error"
                          />
                        )}
                        {item.status === "skipped" && (
                          <span
                            className="shrink-0 h-2 w-2 rounded-full bg-amber-400"
                            title="Skipped"
                          />
                        )}
                        <span
                          className="font-medium text-gray-800 text-xs truncate"
                          title={item.name}
                        >
                          {item.name}
                        </span>
                        {item.checkCount > 1 && (
                          <span className="shrink-0 text-[10px] text-gray-500 bg-gray-100 rounded px-1 py-0.5">
                            batch {item.checkCount}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-700 whitespace-nowrap ml-2">
                        ${item.cost.toFixed(4)}
                      </span>
                    </div>
                    {item.checkNames && item.checkNames.length > 1 && (
                      <div
                        className="text-[10px] text-gray-400 mb-1 truncate"
                        title={item.checkNames.join(", ")}
                      >
                        {item.checkNames.join(", ")}
                      </div>
                    )}
                    {item.errors && item.errors.length > 0 && (
                      <div className="mb-1.5">
                        {item.errors.map((err) => (
                          <p
                            key={err}
                            className="text-[10px] text-red-600 leading-tight"
                            title={err}
                          >
                            {err.length > 120 ? `${err.slice(0, 120)}...` : err}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                      <div>
                        <div className="text-gray-400">Model</div>
                        <div
                          className="font-medium text-gray-600 truncate"
                          title={item.model}
                        >
                          {item.model}
                        </div>
                        {item.fallbackFrom && (
                          <div
                            className="text-[9px] text-amber-600 bg-amber-50 rounded px-1 py-0.5 mt-0.5 inline-block"
                            title={`Context overflow: ${item.fallbackFrom} → ${item.model}`}
                          >
                            fallback from {item.fallbackFrom}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-gray-400">Tokens</div>
                        <div className="font-medium text-gray-600">
                          {item.tokens.total.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400">In / Out</div>
                        <div className="font-medium text-gray-600">
                          {item.tokens.prompt.toLocaleString()} /{" "}
                          {item.tokens.completion.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400">Time</div>
                        <div className="font-medium text-gray-600">
                          {item.durationMs > 0
                            ? formatDuration(item.durationMs)
                            : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rate Limit (collapsible) */}
        {rateLimitInfo && (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => setShowRateLimit(!showRateLimit)}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors w-full"
            >
              {showRateLimit ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              Rate Limit (last snapshot)
            </button>
            {showRateLimit && (
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2.5">
                {/* Timestamp */}
                <div className="text-[10px] text-gray-400">
                  Captured:{" "}
                  {new Date(rateLimitInfo.capturedAt).toLocaleString()}
                </div>
                {/* Requests */}
                <div>
                  <div className="flex justify-between items-baseline text-xs mb-0.5">
                    <span className="text-gray-500 font-medium">Requests</span>
                    <span className="text-gray-400">
                      reset: {rateLimitInfo.resetRequests || "—"}
                    </span>
                  </div>
                  <div className="text-xs font-medium text-gray-700">
                    {rateLimitInfo.remainingRequests.toLocaleString()} /{" "}
                    {rateLimitInfo.limitRequests.toLocaleString()}
                  </div>
                </div>
                {/* Tokens */}
                <div>
                  <div className="flex justify-between items-baseline text-xs mb-0.5">
                    <span className="text-gray-500 font-medium">Tokens</span>
                    <span className="text-gray-400">
                      reset: {rateLimitInfo.resetTokens || "—"}
                    </span>
                  </div>
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    {rateLimitInfo.remainingTokens.toLocaleString()} /{" "}
                    {rateLimitInfo.limitTokens.toLocaleString()}
                  </div>
                  {/* Progress bar */}
                  {rateLimitInfo.limitTokens > 0 &&
                    (() => {
                      const pct =
                        (rateLimitInfo.remainingTokens /
                          rateLimitInfo.limitTokens) *
                        100;
                      return (
                        <div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                pct > 50
                                  ? "bg-green-500"
                                  : pct > 20
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                              }`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5 text-right">
                            {pct.toFixed(1)}% remaining
                          </div>
                        </div>
                      );
                    })()}
                </div>
              </div>
            )}
          </div>
        )}

        {modelUsage.length === 0 && perCheckItems.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-2">
            Usage breakdown not available for this run
          </div>
        )}

        {/* Cache Management */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-500 mb-2">
            Cache Management
          </div>
          <button
            type="button"
            onClick={onOpenCacheManagement}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
          >
            <Database size={12} />
            Open Cache Management
          </button>
        </div>
      </div>
    </>
  );
}

export default AICheckWorkspace;
